// src/App.js
import React, { useState, useMemo, useRef, useEffect } from 'react';
import './App.css';

// State management utilities
import { saveStateToLocalStorage, loadStateFromLocalStorage } from './utils/stateManager';
import { Upload, Download } from 'react-feather';
import StandardDeductionQuestions from './components/StandardDeductionQuestions';

// Data, Constants, and ALL Components
import { initialIncome, initialExpenses, initialAssets, initialInvestments, initialOtherData, initialW2s, initialRentals, initialHouseholdWages, initialTipIncome, initialWaiverPayments } from './mockData';
import { getTaxConstants } from './taxConstants';
import TabNavigation from './components/TabNavigation';
import TotalsSummary from './components/TotalsSummary';
import FilingStatusSelector from './components/FilingStatusSelector';
import W2Grid from './components/W2Grid';
import IncomeGrid from './components/IncomeGrid';
import ExpensesGrid from './components/ExpensesGrid';
import AssetsGrid from './components/AssetsGrid';
import InvestmentsGrid from './components/InvestmentsGrid';
import ItemizedDeductions from './components/ItemizedDeductions';
import CreditsAndAdjustments from './components/CreditsAndAdjustments';
import AuditDefenseNote from './components/AuditDefenseNote';
import RentalIncomeGrid from './components/RentalIncomeGrid';
import Alerts from './components/Alerts';
import CalculationCheck from './components/CalculationCheck';
import FilingPage from './components/FilingPage';
import ReadyToFile from './components/ReadyToFile';
import FilingStatusModal from './components/FilingStatusModal';
/* import ChatInterface from './components/ChatInterface';
import ChatModal from './components/ChatModal'; // Import the new component
import { MessageSquare } from 'react-feather'; // Import icon for the FAB */
import { DateRangePicker } from './components/DateRangePicker'; 
import { autoMapByRegex } from './utils/autoMapper.js';
import { addYears, subYears } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DigitalAssetQuestion from './components/DigitalAssetQuestion';
import DependentsForm from './components/DependentsForm';
import HouseholdWagesGrid from './components/HouseholdWagesGrid';
import TipIncomeGrid from './components/TipIncomeGrid';
import WaiverPaymentsGrid from './components/WaiverPaymentsGrid';
import Form8919Questions from './components/Form8919Questions';
import OtherIncome from './components/OtherIncome';
import CombatPay from './components/CombatPay';
import InterestIncome from './components/InterestIncome';
import BusinessIncome from './components/BusinessIncome';
import { initialScheduleC } from './mockData';
import logo from './redsystem-logo.png';

import SideNav from './components/SideNav';

import { ChevronsRight, ChevronsLeft } from 'react-feather';
import ScheduleE from './components/ScheduleE';
import { initialScheduleE } from './mockData';
import { calculateScheduleD } from './utils/schedDCalculator';
import IncomeSourceSelector from 'components/IncomeSourceSelector';
// Top of App.js
import Schedule1AInputs from './components/Schedule1AInputs';
import Footer from 'components/Footer';

// --- ADD THIS CONSTANT NEAR THE TOP OF THE FILE ---
const RELATIONSHIP_OPTIONS = [
  'Son', 'Daughter', 'Foster Child', 'Brother', 'Sister', 'Grandchild',
  'Parent', 'Grandparent', 'Niece', 'Nephew', 'Aunt', 'Uncle', 'Other'
];


const checkQualifications = (dependent, taxYear) => {
  const qualifications = {
    isQualifyingChildForCTC: false,
    isQualifyingRelativeForODC: false,
    isQualifyingPersonForCare: false,
  };
  if (!dependent.dob || !dependent.relationship) return qualifications;
  const birthDate = new Date(dependent.dob);
  const age = taxYear - birthDate.getFullYear();

  const isChildRelationship = ['Son', 'Daughter', 'Foster Child', 'Brother', 'Sister', 'Grandchild', 'Niece', 'Nephew'].includes(dependent.relationship);
  if (isChildRelationship && age < 17) {
    qualifications.isQualifyingChildForCTC = true;
  }

  if (!qualifications.isQualifyingChildForCTC) {
    const meetsRelationshipTest = dependent.livedWithYou || RELATIONSHIP_OPTIONS.includes(dependent.relationship);
    const meetsGrossIncomeTest = dependent.grossIncome < 4700;
    const meetsSupportTest = dependent.providedHalfSupport;
    if (meetsRelationshipTest && meetsGrossIncomeTest && meetsSupportTest) {
      qualifications.isQualifyingRelativeForODC = true;
    }
  }

  if (age < 13 || dependent.isDisabled) {
    qualifications.isQualifyingPersonForCare = true;
  }
  return qualifications;
};

// Define the initial structure of our application's state
const initialAppState = {
  w2Data: initialW2s,
  incomeData: initialIncome,
  householdWagesData: initialHouseholdWages,
  tipIncomeData: initialTipIncome,
  waiverPaymentsData: initialWaiverPayments,
  expensesData: initialExpenses,
  assetsData: initialAssets,
  investmentsData: initialInvestments,
  formData:  { initialOtherData, incomeVisibility: { w2: true } },
  rentalData: initialRentals,
  careProviders: [],
  isChatVisible: true,
  guideStep: 1,
  scheduleCData: initialScheduleC,
  scheduleEData: initialScheduleE,
};



const getNewItemTemplate = (key) => {
  const base = { id: `${key.replace('Data', '')}-${Date.now()}` };
  switch (key) {
    case 'w2Data':
      return { ...base, employer: 'New Employer', wages: 0, federalTaxWithheld: 0 };
    case 'incomeData':
      return { ...base, category: 'Freelance', description: 'New Income Item', amount: 0, federalTax: 15, localTax: 5 };
    case 'householdWagesData':
      return { ...base, description: 'e.g., Nanny services', amount: 0 };
    case 'tipIncomeData':
      return { ...base, description: 'e.g., Cash tips from restaurant', amount: 0 };
    case 'waiverPaymentsData':
      return { ...base, description: 'e.g., In-home support services', amount: 0 };
    case 'expensesData':
      return { ...base, isDeductible: true, category: 'Office Supplies', description: 'New Expense', amount: 0 };
    case 'assetsData':
      return { ...base, description: 'New Asset', purchaseDate: new Date().toISOString().slice(0, 10), cost: 1000, usefulLife: 5 };
    case 'investmentsData':
      return { ...base, description: 'New Investment', purchaseDate: '', costBasis: 0, saleDate: '', saleProceeds: 0 };
    case 'rentalData':
      return { ...base, description: 'New Property', rentsReceived: 0, expenses: { advertising: 0, repairs: 0, insurance: 0, propertyTaxes: 0 } };
    default:
      return { ...base };
  }
};

const GUIDE_STEPS = [
  { id: 1, title: "Welcome! Let's get started.", text: "This guide will walk you through every step. First, please select the tax year you're filing for below." },
  { id: 2, title: "Step 2: Income", text: "Great! Now let's start by adding your income sources for the year." },
  // ... we can add more steps here later
];

function App() {
  // --- STATE MANAGEMENT ---
  // --- CORRECTED STATE INITIALIZATION ---
// 1. Define the default year first so we can use it immediately
const DEFAULT_YEAR = new Date().getFullYear() - 1;
const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);

// 2. Initialize appState by trying to load the DEFAULT_YEAR immediately
// This prevents the "Save" effect from overwriting your data with an empty state on load
const [appState, setAppState] = useState(() => {
  const saved = loadStateFromLocalStorage(DEFAULT_YEAR);
  if (saved) {
    // Perform the deep merge here to ensure new fields are added to old saves
    return {
      ...initialAppState,
      ...saved,
      formData: {
        ...initialOtherData,
        ...saved.formData
      }
    };
  }
  return initialAppState;
});

const [activeTab, setActiveTab] = useState('Income');
  const importFileRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  /* const [isChatSticky, setIsChatSticky] = useState(false);
  const chatWrapperRef = useRef(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const chatColumnRef = useRef(null);
  const initialChatTop = useRef(0); */
  // 2. UPDATE: Initialize Modal State based on the loaded appState
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(() => {
    // If status is NOT confirmed, open modal. If it IS confirmed, keep closed.
    return !appState.formData?.isFilingStatusConfirmed;
  });
  
  const [dateRange, setDateRange] = useState(null);
  const [guideStep, setGuideStep] = useState(1);
  const [isTxfModalOpen, setIsTxfModalOpen] = useState(false);

  const handleCareProvidersUpdate = (newProviders) => {
    // Create a new formData object that includes the updated providers list
    const newFormData = { ...appState.formData, careProviders: newProviders };
    // Update the entire formData object in the main state
    updateState('formData', newFormData);
  };
  const handleDependentCareBenefitsUpdate = (newBenefits) => {
    const newFormData = { ...appState.formData, dependentCareBenefits: newBenefits };
    updateState('formData', newFormData);
  };
  const handleScheduleCUpdate = (newData) => {
  setAppState(prev => ({ ...prev, scheduleCData: newData }));
};

const handleScheduleEUpdate = (newData) => {
  setAppState(prev => ({ ...prev, scheduleEData: newData }));
};


  // src/App.js
  useEffect(() => {
    if (selectedYear) {
      setDateRange({
        from: new Date(selectedYear, 0, 1),
        to: new Date(selectedYear, 11, 31)
      });
    }
  }, [selectedYear]);

   

// 3. UPDATE: Consolidate Data Loading & Modal Logic into ONE Effect
  useEffect(() => {
    if (selectedYear) {
      const persistedState = loadStateFromLocalStorage(selectedYear);
      
      if (persistedState) {
        const mergedState = {
          ...initialAppState,
          ...persistedState,
          formData: {
            ...initialOtherData,
            incomeVisibility: { w2: true },
            ...persistedState.formData
          }
        };
        setAppState(mergedState);
        
        // Check the LOADED state to decide if modal should open
        if (!mergedState.formData.isFilingStatusConfirmed) {
          setIsStatusModalOpen(true);
        } else {
          setIsStatusModalOpen(false);
        }

      } else {
        // No data for this year? Reset and Open Modal
        setAppState({ ...initialAppState, guideStep: 1 });
        setIsStatusModalOpen(true);
      }
    }
  }, [selectedYear]);

  // EFFECT 2: Save data whenever the appState or selectedYear changes
  useEffect(() => {
    if (selectedYear) {
      saveStateToLocalStorage(appState, selectedYear);
    }
  }, [appState, selectedYear]);

  /*
  useEffect(() => {
    const chatWrapper = chatWrapperRef.current;
    if (!chatWrapper) return;
    const originalTop = chatWrapper.offsetTop;
    const handleScroll = () => {
      if (window.scrollY > originalTop - 20) setIsChatSticky(true);
      else setIsChatSticky(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  */

  // --- VALIDATION ENGINE ---
  useEffect(() => {
    const newErrors = {};
    const { formData } = appState;
    const currentConstants = getTaxConstants(selectedYear);
    const IRA_LIMIT = currentConstants.LIMITS.IRA_CONTRIBUTION_LIMIT;

    if (parseFloat(formData?.itemizedDeductions?.medicalExpenses) < 0) newErrors.medicalExpenses = "Cannot be negative.";
    if (parseFloat(formData?.itemizedDeductions?.stateAndLocalTaxes) < 0) newErrors.stateAndLocalTaxes = "Cannot be negative.";
    if (parseFloat(formData?.itemizedDeductions?.mortgageInterest) < 0) newErrors.mortgageInterest = "Cannot be negative.";
    if (parseFloat(formData?.itemizedDeductions?.charitableDonations) < 0) newErrors.charitableDonations = "Cannot be negative.";
    if (parseFloat(formData.iraContribution) < 0) newErrors.iraContribution = "Cannot be negative.";
    if (parseFloat(formData.studentLoanInterest) < 0) newErrors.studentLoanInterest = "Cannot be negative.";
    if (parseFloat(formData.retirementContributions) < 0) newErrors.retirementContributions = "Cannot be negative.";

    if (parseFloat(formData.iraContribution) > IRA_LIMIT) {
      newErrors.iraContribution = `Contribution cannot exceed the annual limit of $${IRA_LIMIT}.`;
    }

    setErrors(newErrors);
  }, [appState.formData]);

  const handleExportState = () => {
    if (!selectedYear) {
      alert("Please select a tax year before exporting.");
      return;
    }
    const stateJson = JSON.stringify(appState, null, 2);
    const blob = new Blob([stateJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tax-return-${selectedYear}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleTriggerImport = () => {
    importFileRef.current.click();
  };

  const handleImportState = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedState = JSON.parse(e.target.result);
        // Basic validation
        if (importedState.w2Data && importedState.formData) {
          setAppState(importedState);
          alert(`Successfully imported data for the year ${selectedYear}. The page will now use this new data.`);
        } else {
          throw new Error("Invalid file format.");
        }
      } catch (error) {
        alert("Error importing file. Please ensure it is a valid tax return file.");
        console.error("Import Error:", error);
      }
    };
    reader.readAsText(file);
    event.target.value = null;
  };

  const filterDataByDate = (data) => {
    if (!dateRange?.from) return []; // Return nothing if no date is set yet
    // Ensure 'to' date includes the entire day
    const endDate = dateRange.to ? new Date(dateRange.to.setHours(23, 59, 59, 999)) : dateRange.from;
    
    return data.filter(item => {
      // Use the timestamp for filtering as it's the most consistent date field
      if (!item.timestamp) return false;
      const itemDate = new Date(item.timestamp);
      return itemDate >= dateRange.from && itemDate <= endDate;
    });
  };

  // Create memoized, filtered versions of all your data
  const filteredW2Data = useMemo(() => appState.w2Data, [appState.w2Data]);
  const filteredIncomeData = useMemo(() => appState.incomeData, [appState.incomeData]);
  const filteredExpensesData = useMemo(() => appState.expensesData, [appState.expensesData]);
  const filteredAssetsData = useMemo(() => appState.assetsData, [appState.assetsData]);
  const filteredInvestmentsData = useMemo(() => appState.investmentsData, [appState.investmentsData]);
  const filteredRentalData = useMemo(() => appState.rentalData, [appState.rentalData]);

  const handlePreviousYear = () => {
  if (selectedYear) {
    setSelectedYear(selectedYear - 1);
  }
};

const handleNextYear = () => {
  if (selectedYear) {
    setSelectedYear(selectedYear + 1);
  }
};// src/App.js

// ... (inside the App component)

const handleDependentsUpdate = (newDependents) => {
    const newFormData = { ...appState.formData, dependents: newDependents };
    updateState('formData', newFormData);
  };

const handleDateChange = (newDateRange) => {
  // Update the dateRange state immediately for a responsive UI in the picker.
  setDateRange(newDateRange);

  // If the user selected a date, check if the year is different from our master state.
  if (newDateRange?.from) {
    const newYear = newDateRange.from.getFullYear();
    if (newYear !== selectedYear) {
      // If the year has changed, update the master 'selectedYear' state.
      // This will trigger our main useEffect to reload all the data.
      setSelectedYear(newYear);
    }
  }
};

  // src/App.js

// ... (inside the App component, after your useState hooks)

// --- NEW, CORRECTED EFFECT: Manages body scroll for ALL modals ---
// UPDATE THIS EFFECT
// UPDATE THIS EFFECT
useEffect(() => {
  // Only check Status Modal now (since others are removed/commented)
  const isAnyModalOpen = isStatusModalOpen;

  if (isAnyModalOpen) {
    document.body.classList.add('modal-open');
  } else {
    document.body.classList.remove('modal-open');
  }

  return () => {
    document.body.classList.remove('modal-open');
  };
}, [isStatusModalOpen]);

  const handleSmartImport = (file, requiredFields, onComplete) => {
    // 1. Get a sample
    const dataSample = file.data.slice(0, 5);
    
    // 2. Try to auto-map
    const autoMapping = autoMapByRegex(file.headers, dataSample, requiredFields);

    if (autoMapping) {
      // SUCCESS: Process immediately. Pass file.data directly.
      onComplete(autoMapping, file.data);
    } else {
      // FAILURE: Just alert the user since Mapper is removed
      alert(`Could not automatically match columns. Please ensure your CSV has standard headers like: ${requiredFields.join(', ')}`);
    }
  };

  // --- GLOBAL: AUTO-SELECT INPUT TEXT ON FOCUS ---
  useEffect(() => {
    const handleFocus = (e) => {
      const target = e.target;
      // Check if the target is an input field of the correct type
      if (target.tagName === 'INPUT' && ['text', 'number', 'tel', 'email'].includes(target.type)) {
        // A small timeout is required to override the browser's default behavior 
        // which tries to place the cursor exactly where you clicked.
        setTimeout(() => target.select(), 10);
      }
    };

    // 'focusin' bubbles up, allowing us to catch focus events from anywhere in the app
    document.addEventListener('focusin', handleFocus);
    
    return () => {
      document.removeEventListener('focusin', handleFocus);
    };
  }, []);

  const incomeComponents = useMemo(() => {
    const { 
      w2Data = [], 
      householdWagesData = [], 
      tipIncomeData = [], 
      waiverPaymentsData = [],
      formData 
    } = appState;

    // --- W-2 Income ---
    const calculatedW2Wages = w2Data.reduce((sum, w2) => sum + (parseFloat(w2.wages) || 0), 0);
    const manualWagesInput = parseFloat(formData.manualW2Wages);
    const totalW2Wages = !isNaN(manualWagesInput) ? manualWagesInput : calculatedW2Wages;

    // --- Household Wages ---
    const calculatedHousehold = (householdWagesData || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const manualHousehold = parseFloat(formData.manualHouseholdWages);
    const totalHouseholdWages = !isNaN(parseFloat(formData.manualHouseholdWages)) 
    ? Math.max(0, parseFloat(formData.manualHouseholdWages)) 
    : (householdWagesData || []).reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

    // --- Tip Income ---
    const calculatedTips = (tipIncomeData || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const manualTips = parseFloat(formData.manualTipIncome);
    const totalUnreportedTips = !isNaN(manualTips) ? manualTips : calculatedTips;

    // --- Waiver Payments ---
    const calculatedWaiver = (waiverPaymentsData || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const manualWaiver = parseFloat(formData.manualWaiverPayments);
    const totalWaiverPayments = !isNaN(manualWaiver) ? manualWaiver : calculatedWaiver;

    const totalTaxableInterest = parseFloat(formData.taxableInterest) || 0;
    const totalTaxableIraDistributions = parseFloat(formData.taxableIraDistributions) || 0;
    const totalTaxablePensionsAnnuities = parseFloat(formData.taxablePensionsAnnuities) || 0;
    const totalTaxableSocialSecurity = parseFloat(formData.taxableSocialSecurityBenefits) || 0;
    
    const totalStrikeBenefits = parseFloat(formData.strikeBenefits) || 0;
    const totalDisabilityPension = parseFloat(formData.disabilityPensionPreRetirement) || 0;
    const totalCorrectiveDistributions = parseFloat(formData.correctiveDistributions) || 0;
    const form_8919_total_wages = (formData?.form8919?.firms || []).reduce((sum, firm) => sum + (parseFloat(firm.wages) || 0), 0);

    const totalMiscellaneousIncome = totalHouseholdWages +
                                   totalUnreportedTips +
                                   totalTaxableInterest +
                                   totalTaxableIraDistributions +
                                   totalTaxablePensionsAnnuities +
                                   totalTaxableSocialSecurity +
                                   totalWaiverPayments +
                                   totalStrikeBenefits +
                                   totalDisabilityPension +
                                   totalCorrectiveDistributions +
                                   form_8919_total_wages;

    return {
      totalW2Wages,
      totalMiscellaneousIncome,
      totalHouseholdWages,
      totalUnreportedTips,
      totalWaiverPayments
    };
  }, [appState]);

const totals = useMemo(() => {
  const { 
    w2Data = [],
    incomeData = [],
    householdWagesData = [],
    tipIncomeData = [],
    waiverPaymentsData = [],
    expensesData = [],
    assetsData = [],
    investmentsData = [],
    rentalData = [],
    formData, 
    careProviders = []  
  } = appState;

  
  
  const taxYear = selectedYear || new Date().getFullYear();
  // Safety fallback: if filingStatus is missing, default to 'Single'
const filingStatus = formData?.filingStatus || 'Single';
  const yearData = getTaxConstants(taxYear);
  
  const limits = yearData.LIMITS;       
  const statusData = yearData[filingStatus] || yearData['Single'];
  
  const SOCIAL_SECURITY_LIMIT = limits.SOCIAL_SECURITY_WAGE_BASE;
  const SOCIAL_SECURITY_RATE = limits.SOCIAL_SECURITY_RATE;
  const MEDICARE_RATE = limits.MEDICARE_RATE;

  const taxableDependentCareBenefits = formData?.dependentCare?.form2441_line24 || 0;
  

  let taxableAdoptionBenefits = 0;
  if (formData?.adoptionCredit?.isLimitationGreaterThanBenefits) {
    taxableAdoptionBenefits = formData?.adoptionCredit.finalBenefitCalculation || 0;
  }

  const form8919Data = formData?.form8919 || { firms: [] };
  
  const form_8919_total_wages = (form8919Data.firms || []).reduce(
    (sum, firm) => sum + (parseFloat(firm.wages) || 0),
    0
  );

  let form_8919_final_tax = 0;

  if (form_8919_total_wages > 0) {
    const total_ss_earnings_before_8919 = 
      (parseFloat(form8919Data.totalSSWages) || 0) +
      (parseFloat(form8919Data.totalSSTips) || 0) +
      (parseFloat(form8919Data.unreportedTipsSubjectToSS) || 0);
    
    const remaining_ss_room = Math.max(0, SOCIAL_SECURITY_LIMIT - total_ss_earnings_before_8919);
    const taxable_8919_ss_wages = Math.min(form_8919_total_wages, remaining_ss_room);
    const form_8919_ss_tax = taxable_8919_ss_wages * SOCIAL_SECURITY_RATE;
    const form_8919_medicare_tax = form_8919_total_wages * MEDICARE_RATE;
    
    const total_8919_tax = form_8919_ss_tax + form_8919_medicare_tax;
    const tax_already_paid = parseFloat(form8919Data.taxPaidOnTips) || 0;
    form_8919_final_tax = Math.max(0, total_8919_tax - tax_already_paid);
  }

   // ===========================================================================
    // SCHEDULE D & FORM 8949 DATA PREPARATION
    // ===========================================================================

    // 1. Helper to calculate gains
    const getTerm = (purchaseDate, saleDate) => {
      if (!purchaseDate || !saleDate) return 'Short'; 
      const start = new Date(purchaseDate);
      const end = new Date(saleDate);
      const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      return diffDays > 365 ? 'Long' : 'Short';
    };

    // 2. Initialize Buckets
    // Standard Stocks (1099-B)
    let f8949_A_items = []; // ST, Basis Reported
    let f8949_B_items = []; // ST, Basis Not Reported
    let f8949_C_items = []; // ST, Not on 1099-B
    let f8949_D_items = []; // LT, Basis Reported
    let f8949_E_items = []; // LT, Basis Not Reported
    let f8949_F_items = []; // LT, Not on 1099-B
    
    // Digital Assets (1099-DA) - Placeholders for future use
    let f8949_G_items = []; 
    let f8949_H_items = []; 
    let f8949_I_items = []; 
    let f8949_J_items = []; 
    let f8949_K_items = []; 
    let f8949_L_items = []; 

    // 3. Sort Investments into Buckets
    investmentsData.forEach(item => {
      const term = getTerm(item.purchaseDate, item.saleDate);
      const status = item.reportingStatus || 'Basis Reported';
      const proceeds = parseFloat(item.saleProceeds || 0);
      const cost = parseFloat(item.costBasis || 0);
      const adjAmount = parseFloat(item.adjustmentAmount || 0); 
      
      // Determine Code (W for Wash Sale if not specified)
      let code = item.adjustmentCode || '';
      if (!code && adjAmount !== 0) code = 'W'; // Default to Wash Sale if amount exists

      const gain = proceeds - cost + adjAmount;

      const cleanItem = {
        description: item.description,
        dateAcquired: item.purchaseDate,
        dateSold: item.saleDate,
        proceeds: proceeds,
        costBasis: cost,
        code: code,
        adjustmentAmount: adjAmount,
        gainLoss: gain
      };

      // Logic to separate Digital Assets could go here (e.g., if item.type === 'Crypto')
      // For now, we assume everything is Standard 1099-B (A-F)
      
      if (term === 'Short') {
        if (status === 'Basis Reported') f8949_A_items.push(cleanItem);
        else if (status === 'Basis Not Reported') f8949_B_items.push(cleanItem);
        else f8949_C_items.push(cleanItem);
      } else {
        if (status === 'Basis Reported') f8949_D_items.push(cleanItem);
        else if (status === 'Basis Not Reported') f8949_E_items.push(cleanItem);
        else f8949_F_items.push(cleanItem);
      }
    });

    // 4. IMPLEMENT EXCEPTION 1 (Filter "Clean" vs "Adjusted")
    // Clean items go directly to Schedule D Line 1a/8a.
    // Adjusted items go to Form 8949 Box A/D.
    
    const isClean = (item) => item.adjustmentAmount === 0 && (!item.code || item.code === '');

    // Split Box A (Short-Term)
    const st_A_clean_items = f8949_A_items.filter(isClean);
    const st_A_adjusted_items = f8949_A_items.filter(i => !isClean(i));

    // Split Box D (Long-Term)
    const lt_D_clean_items = f8949_D_items.filter(isClean);
    const lt_D_adjusted_items = f8949_D_items.filter(i => !isClean(i));

    // 5. Calculate Sums
    const sumBucket = (items) => items.reduce((acc, item) => ({
      proceeds: acc.proceeds + item.proceeds,
      cost: acc.cost + item.costBasis,
      adjustments: acc.adjustments + item.adjustmentAmount,
      gain: acc.gain + item.gainLoss
    }), { proceeds: 0, cost: 0, adjustments: 0, gain: 0 });

    // Schedule D Line 1a (Clean Short-Term)
    const schedD_line1a_totals = sumBucket(st_A_clean_items);
    
    // Schedule D Line 8a (Clean Long-Term)
    const schedD_line8a_totals = sumBucket(lt_D_clean_items);

    // Form 8949 Totals (Adjusted A/D + All B/C/E/F)
    const f8949_A_totals = sumBucket(st_A_adjusted_items);
    const f8949_B_totals = sumBucket(f8949_B_items);
    const f8949_C_totals = sumBucket(f8949_C_items);
    
    const f8949_D_totals = sumBucket(lt_D_adjusted_items);
    const f8949_E_totals = sumBucket(f8949_E_items);
    const f8949_F_totals = sumBucket(f8949_F_items);

    // 6. Final Schedule D Lines
    // Line 1a: Clean totals
    const schedD_line1a = schedD_line1a_totals.gain;
    
    // Line 1b: Adjusted totals (from Form 8949 Box A)
    const schedD_line1b = f8949_A_totals.gain;
    
    // Line 2 (Box B) & Line 3 (Box C)
    const schedD_line2 = f8949_B_totals.gain;
    const schedD_line3 = f8949_C_totals.gain;

    // Line 7: Net Short-Term Gain/Loss
    const schedD_line7 = schedD_line1a + schedD_line1b + schedD_line2 + schedD_line3 + 
                         (parseFloat(formData.capitalLossCarryoverShort) || 0) * -1;

    // Line 8a: Clean totals
    const schedD_line8a = schedD_line8a_totals.gain;

    // Line 8b: Adjusted totals (from Form 8949 Box D)
    const schedD_line8b = f8949_D_totals.gain;

    // Line 9 (Box E) & Line 10 (Box F)
    const schedD_line9 = f8949_E_totals.gain;
    const schedD_line10 = f8949_F_totals.gain;

    // Line 15: Net Long-Term Gain/Loss
    const schedD_line15 = schedD_line8a + schedD_line8b + schedD_line9 + schedD_line10 + 
                          (parseFloat(formData.capitalGainDistributions) || 0) +
                          (parseFloat(formData.capitalLossCarryoverLong) || 0) * -1;

    // Line 16: Combined Net Gain/Loss
    const schedD_line16 = schedD_line7 + schedD_line15;

  let schedD_line21 = 0;
  let newCapitalLossCarryover = 0;

  if (schedD_line16 >= 0) {
    schedD_line21 = 0; 
    newCapitalLossCarryover = 0;
  } else {
    const limit = filingStatus === 'Married Filing Separately' 
      ? limits.CAPITAL_LOSS_LIMIT_MFS 
      : limits.CAPITAL_LOSS_LIMIT;
    const lossMagnitude = Math.abs(schedD_line16);
    const deductibleLoss = Math.min(lossMagnitude, limit);
    
    schedD_line21 = deductibleLoss * -1;
    newCapitalLossCarryover = lossMagnitude - deductibleLoss;
  }

  const hasAssetSales = investmentsData.length > 0;
  const hasCapGainDist = (parseFloat(formData.capitalGainDistributions) || 0) > 0;
  const hasLossCarryover = (parseFloat(formData.capitalLossCarryover) || 0) > 0;
  const isScheduleDRequired = hasAssetSales || hasCapGainDist || hasLossCarryover;

  // Determine the final capital gain/loss to use for income
  let capitalGainsForIncome;
    const manualGainsInput = parseFloat(formData.manualCapitalGains);
  
    // FIX: Check !isNaN
    if (!isNaN(manualGainsInput)) {
      capitalGainsForIncome = manualGainsInput;
    } else {
      // Fallback to calculated logic
      if (schedD_line16 >= 0) {
        capitalGainsForIncome = schedD_line16;
      } else {
        const limit = filingStatus === 'Married Filing Separately' 
          ? limits.CAPITAL_LOSS_LIMIT_MFS 
          : limits.CAPITAL_LOSS_LIMIT;
        const lossMagnitude = Math.abs(schedD_line16);
        const deductibleLoss = Math.min(lossMagnitude, limit);
        
        capitalGainsForIncome = deductibleLoss * -1;
      }
    }

  const reportableLTCG = schedD_line15 > 0 ? Math.min(schedD_line15, Math.max(0, capitalGainsForIncome)) : 0;

  const safeHouseholdWagesData = householdWagesData || [];
  const safeTipIncomeData = tipIncomeData || [];
  const safeWaiverPaymentsData = waiverPaymentsData || [];

  const totalW2Wages = !isNaN(parseFloat(formData.manualW2Wages)) ? parseFloat(formData.manualW2Wages) : w2Data.reduce((sum, w2) => sum + parseFloat(w2.wages || 0), 0);
    
    
  // --- 2. Business Income ---
  const totalFreelanceIncome = incomeData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
  
  const totalHouseholdWages = !isNaN(parseFloat(formData.manualHouseholdWages)) ? parseFloat(formData.manualHouseholdWages) : (householdWagesData || []).reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    
    const totalTipIncome = !isNaN(parseFloat(formData.manualTipIncome)) ? parseFloat(formData.manualTipIncome) : (tipIncomeData || []).reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    
    const totalWaiverPayments = !isNaN(parseFloat(formData.manualWaiverPayments)) ? parseFloat(formData.manualWaiverPayments) : (waiverPaymentsData || []).reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  const totalBusinessExpenses = expensesData.filter(i => i.isDeductible).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const totalDepreciation = !isNaN(parseFloat(formData.manualDepreciation)) 
      ? parseFloat(formData.manualDepreciation) 
      : assetsData.reduce((sum, item) => sum + ((parseFloat(item.cost) || 0) / (parseFloat(item.usefulLife) || 1)), 0);
    const scheduleCProfit = (appState.scheduleCData || []).reduce((total, biz) => {
        const grossReceipts = parseFloat(biz.grossReceipts) || 0;
        const returns = parseFloat(biz.returnsAndAllowances) || 0;
        const otherInc = parseFloat(biz.otherIncome) || 0;
        
        // COGS
        const inventoryStart = parseFloat(biz.inventoryBeginning) || 0;
        const purchases = parseFloat(biz.purchases) || 0;
        const labor = parseFloat(biz.costOfLabor) || 0;
        const materials = parseFloat(biz.materialsAndSupplies) || 0;
        const inventoryEnd = parseFloat(biz.inventoryEnd) || 0;
        const cogs = (inventoryStart + purchases + labor + materials) - inventoryEnd;

        const grossIncome = (grossReceipts - returns) - cogs + otherInc;

        // Expenses
        let totalExpenses = 0;
        const expenseFields = [
            'advertising', 'carAndTruckExpenses', 'commissions', 'contractLabor', 'depletion', 
            'depreciation', 'employeeBenefitPrograms', 'insurance', 'mortgageInterest', 
            'otherInterest', 'legalAndProfessional', 'officeExpense', 'pensionAndProfitSharing', 
            'rentVehicles', 'rentMachinery', 'repairsAndMaintenance', 'supplies', 
            'taxesAndLicenses', 'travel', 'utilities', 'wages'
        ];
        
        expenseFields.forEach(f => totalExpenses += (parseFloat(biz[f]) || 0));
        
        // Meals (50%)
        totalExpenses += (parseFloat(biz.meals) || 0) * 0.50;

        // Other Expenses
        (biz.otherExpenses || []).forEach(e => totalExpenses += (parseFloat(e.amount) || 0));

        // Home Office (Simplified)
        const homeArea = parseFloat(biz.homeOffice?.areaUsed) || 0;
        const homeDed = Math.min(homeArea, 300) * 5;

        return total + (grossIncome - totalExpenses - homeDed);
    }, 0);

    // B. Calculate Simple Freelance Grid (Casual/Hobby Income)
    // Note: We keep this for backward compatibility and "Other" income, 
    // but Schedule C is now the primary driver for self-employment.
    const simpleFreelanceIncome = incomeData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    // C. Combine them (Manual override still takes precedence if set)
    const calculatedBusinessIncome = scheduleCProfit + simpleFreelanceIncome;
    
    const manualBusinessInput = parseFloat(formData.manualBusinessIncome);
    const netBusinessIncome = !isNaN(manualBusinessInput) ? manualBusinessInput : calculatedBusinessIncome;
  
  // [NEW CODE TO INSERT]
  // --- SCHEDULE SE CALCULATION (Full Implementation) ---
  let selfEmploymentTax = 0;
  let totalSEIncome = 0;     // Net Profit (Line 2)
  let totalSEGross = 0;      // Gross Income (Needed for Optional Method)
  let totalNotaryExempt = 0;
  let totalCRPExempt = 0;

  // 1. Aggregate Net Profit AND Gross Income from all Schedule C businesses
  (appState.scheduleCData || []).forEach(biz => {
    // Statutory Employees are EXCLUDED from SE Tax
    if (!biz.statutoryEmployee) {
        
        // A. Calculate Gross Income for this business
        const gross = (parseFloat(biz.grossReceipts)||0) - (parseFloat(biz.returnsAndAllowances)||0) - 
                      ((parseFloat(biz.inventoryBeginning)||0) + (parseFloat(biz.purchases)||0) + (parseFloat(biz.costOfLabor)||0) + (parseFloat(biz.materialsAndSupplies)||0) + (parseFloat(biz.otherCosts)||0) - (parseFloat(biz.inventoryEnd)||0)) + 
                      (parseFloat(biz.otherIncome)||0);
        
        // B. Calculate Total Expenses for this business
        let exps = 0;
        const fields = [
            'advertising','carAndTruckExpenses','commissions','contractLabor','depletion',
            'depreciation','employeeBenefitPrograms','insurance','mortgageInterest',
            'otherInterest','legalAndProfessional','officeExpense','pensionAndProfitSharing',
            'rentVehiclesMachinery','rentOtherProperty','repairsAndMaintenance','supplies',
            'taxesAndLicenses','travel','utilities','wages',
            'energyEfficientCommercialBuildingsDeduction'
        ];
        fields.forEach(f => exps += (parseFloat(biz[f])||0));
        exps += (parseFloat(biz.meals)||0) * 0.5; // 50% limit on meals
        (biz.otherExpenses||[]).forEach(e => exps += (parseFloat(e.amount)||0));
        
        // C. Calculate Home Office Deduction (Limited to Tentative Profit)
        const homeDed = Math.min((parseFloat(biz.homeOffice?.areaUsed)||0), 300) * 5;
        const tentative = gross - exps;
        const allowedHome = Math.max(0, Math.min(homeDed, tentative));
        
        // D. Final Net Profit for this business
        const net = tentative - allowedHome;

        // E. Accumulate Totals
        totalSEIncome += net;
        totalSEGross += gross; 
        totalNotaryExempt += (parseFloat(biz.exemptNotaryIncome) || 0);
        totalCRPExempt += (parseFloat(biz.conservationReserveProgramPayments) || 0);
    }
  });

  // 2. Apply Adjustments (Schedule SE Part I)
  // Line 3: Combine lines 1a, 1b, and 2 (Subtract exempt income)
  const combinedSEIncome = totalSEIncome - totalNotaryExempt - totalCRPExempt;

  // 3. Calculate SE Tax Base
  // Line 4a: Multiply Actual Net by 92.35%
  const line4a = combinedSEIncome * 0.9235;
  
  // Line 4b: Optional Methods (Part II)
  let line4bse = 0; 
  const useOptional = formData?.scheduleSE?.useNonfarmOptionalMethod;
  
  // 2024 Constants for Optional Method
  const OPTIONAL_GROSS_LIMIT = 10860; // If gross <= this, use 2/3 gross
  const OPTIONAL_MAX_EARNINGS = 7240; // Max reportable amount

  if (useOptional) {
      // Line 17 Calculation logic
      if (totalSEGross <= OPTIONAL_GROSS_LIMIT) {
          line4bse = totalSEGross * (2/3);
      } else {
          line4bse = OPTIONAL_MAX_EARNINGS; // $7,240
      }
  }

  // Line 4c: Combine lines 4a and 4b
  // If using Optional Method, we generally use the Optional Amount as the tax base
  // because the purpose is to pay tax on a deemed amount when actual income is low/negative.
  let taxableSEBase = 0;
  
  if (useOptional) {
      // If Optional is checked, use the calculated Optional Amount
      taxableSEBase = line4bse;
  } else {
      // Otherwise use the Standard Amount
      taxableSEBase = line4a;
  }

  // 4. Calculate Final Tax
  // Threshold check ($400)
  if (taxableSEBase >= 400) {
    const SS_WAGE_BASE = limits.SOCIAL_SECURITY_WAGE_BASE; // 168,600 for 2024
    
    // Social Security Tax (12.4%)
    // We must account for W-2 wages already taxed
    const wagesSubjectToSS = Math.max(0, SS_WAGE_BASE - totalW2Wages);
    const taxableSEforSS = Math.min(taxableSEBase, wagesSubjectToSS);
    const ssTax = taxableSEforSS * 0.124;

    // Medicare Tax (2.9%) - No limit
    const mediTax = taxableSEBase * 0.029;

    selfEmploymentTax = ssTax + mediTax;
  }
  
  const calculatedRentalIncome = rentalData.reduce((total, property) => {
      const rents = parseFloat(property.rentsReceived) || 0;
      const exp = property.expenses;
      const totalExpenses = (parseFloat(exp.advertising) || 0) + (parseFloat(exp.repairs) || 0) + (parseFloat(exp.insurance) || 0) + (parseFloat(exp.propertyTaxes) || 0);
      return total + (rents - totalExpenses);
    }, 0);
  
    // --- SCHEDULE E CALCULATIONS (2025 Rules) ---
const schedE = appState.scheduleEData || initialScheduleE;

// Part I: Real Estate
let totalRentalIncome = 0;
let totalRentalLoss = 0;

(schedE.properties || []).forEach(prop => {
    const income = (parseFloat(prop.rentsReceived) || 0) + (parseFloat(prop.royaltiesReceived) || 0);
    
    // 2025 Rule: 70 cents per mile
    const autoExp = (parseFloat(prop.autoMiles) || 0) * 0.70 + (parseFloat(prop.autoAndTravel) || 0);
    
    const expenses = 
        (parseFloat(prop.advertising) || 0) +
        autoExp +
        (parseFloat(prop.cleaningAndMaintenance) || 0) +
        (parseFloat(prop.commissions) || 0) +
        (parseFloat(prop.insurance) || 0) +
        (parseFloat(prop.legalAndProfessional) || 0) +
        (parseFloat(prop.managementFees) || 0) +
        (parseFloat(prop.mortgageInterest) || 0) +
        (parseFloat(prop.otherInterest) || 0) +
        (parseFloat(prop.repairs) || 0) +
        (parseFloat(prop.supplies) || 0) +
        (parseFloat(prop.taxes) || 0) +
        (parseFloat(prop.utilities) || 0) +
        (parseFloat(prop.depreciation) || 0);

    let net = income - expenses;

    // Vacation Home Limitation (Line 2 logic)
    const daysRented = parseFloat(prop.fairRentalDays) || 0;
    const daysPersonal = parseFloat(prop.personalUseDays) || 0;
    const isVacationHome = daysPersonal > 14 || daysPersonal > (daysRented * 0.10);

    if (isVacationHome && net < 0) {
        net = 0; // Loss disallowed
    }

    if (net >= 0) totalRentalIncome += net;
    else totalRentalLoss += net;
});

// Part II: Partnerships
let totalPartnershipIncome = 0;
let totalPartnershipLoss = 0;

(schedE.partnerships || []).forEach(k1 => {
    const passiveInc = parseFloat(k1.passiveIncome) || 0;
    const nonPassiveInc = parseFloat(k1.nonPassiveIncome) || 0;
    const passiveLoss = parseFloat(k1.passiveLossAllowed) || 0; 
    const nonPassiveLoss = parseFloat(k1.nonPassiveLoss) || 0;
    const sec179 = parseFloat(k1.section179Deduction) || 0;

    const net = (passiveInc + nonPassiveInc) - (passiveLoss + nonPassiveLoss + sec179);
    
    if (net >= 0) totalPartnershipIncome += net;
    else totalPartnershipLoss += net;
});

// Total Schedule E (Line 41)
const totalScheduleE = totalRentalIncome + totalRentalLoss + totalPartnershipIncome + totalPartnershipLoss;

// Manual Override Logic
const manualRentalInput = parseFloat(formData.manualRentalIncome);
const netRentalIncome = !isNaN(manualRentalInput) ? manualRentalInput : totalScheduleE;
  
  const sched1_line1 = parseFloat(formData.taxableStateRefund) || 0;
  const sched1_line2a = parseFloat(formData.alimonyReceived) || 0;
  const sched1_line3 = netBusinessIncome;
  const sched1_line4 = parseFloat(formData.otherGainsLosses) || 0; // NEW
  const sched1_line5 = netRentalIncome;
  const sched1_line6 = parseFloat(formData.farmIncome) || 0;       // NEW
  const sched1_line7 = parseFloat(formData.unemploymentCompensation) || 0;
  const sched1_line8s = totalWaiverPayments > 0 ? -totalWaiverPayments : 0;
  
  const sched1_line8z = 
    (parseFloat(formData.strikeBenefits) || 0) +
    (parseFloat(formData.juryDutyPay) || 0) + 
    (parseFloat(formData.prizesAndAwards) || 0) +
    (parseFloat(formData.gamblingWinnings) || 0) +
    (parseFloat(formData.netOperatingLoss) || 0) +
    (parseFloat(formData.cancellationOfDebt) || 0) +
    (parseFloat(formData.foreignEarnedIncomeExclusion) || 0) +
    (parseFloat(formData.incomeFrom8853) || 0) +
    (parseFloat(formData.incomeFrom8889) || 0) +
    (parseFloat(formData.alaskaDividends) || 0) +
    (parseFloat(formData.hobbyIncome) || 0) +
    (parseFloat(formData.stockOptions) || 0) +
    (parseFloat(formData.personalPropertyRental) || 0) +
    (parseFloat(formData.olympicPrizeMoney) || 0) +
    (parseFloat(formData.section951a) || 0) +
    (parseFloat(formData.section951Aa) || 0) +
    (parseFloat(formData.excessBusinessLoss) || 0) +
    (parseFloat(formData.ableDistributions) || 0) +
    (parseFloat(formData.scholarships) || 0) +
    (parseFloat(sched1_line8s) || 0) +
    (parseFloat(formData.nonqualifiedPension) || 0) +
    (parseFloat(formData.incarceratedWages) || 0) +
    (parseFloat(formData.digitalAssetOrdinaryIncome) || 0) +
    (parseFloat(formData.rtaaPayments) || 0) +
    (parseFloat(formData.correctiveDistributions) || 0) +
    (parseFloat(formData.disasterReliefPayments) || 0) +
    (parseFloat(formData.coverdellTaxableDistributions) || 0) +
    (parseFloat(formData.line8z_other_amount) || 0);
  
  const sched1_line9 = sched1_line8z;
  const saleOfPersonalItems1099K = parseFloat(formData.saleOfPersonalItems1099K) || 0;
  
  const sched1_line10 = 
    (sched1_line1 ?? 0) + 
    (sched1_line2a ?? 0) + 
    (sched1_line3 ?? 0) + 
    (sched1_line4 ?? 0) + 
    (sched1_line5 ?? 0) + 
    (sched1_line6 ?? 0) + 
    (sched1_line7 ?? 0) + 
    (sched1_line9 ?? 0);

  const sched1_line11 = parseFloat(formData.educatorExpenses) || 0;
  const sched1_line12 = parseFloat(formData.reservistExpenses) || 0; // NEW
  const sched1_line13 = parseFloat(formData.hsaDeduction) || 0;
  const sched1_line14 = parseFloat(formData.movingExpenses) || 0;    // NEW
  const sched1_line15 = selfEmploymentTax * 0.5;
  const sched1_line16 = parseFloat(formData.selfEmployedRetirementDeduction) || 0;
  const sched1_line17 = parseFloat(formData.selfEmployedHealthInsurance) || 0;
  const sched1_line18 = parseFloat(formData.earlyWithdrawalPenalty) || 0;
  const sched1_line19a = parseFloat(formData.alimonyPaid) || 0; 
  const sched1_line20 = parseFloat(formData.iraContribution) || 0;
  const sched1_line21 = parseFloat(formData.studentLoanInterest) || 0;
  const sched1_line23 = parseFloat(formData.archerMsaDeduction) || 0; // NEW
  const sched1_line24a = parseFloat(formData.juryDutyPayTurnedOver) || 0;
  const sched1_line24b = parseFloat(formData.personalPropertyRentalExpenses) || 0;
  const sched1_line24z = parseFloat(formData.otherAdjustments24z) || 0; // NEW
  const totalAdjustmentsSched1 = (sched1_line24a ?? 0) + (sched1_line24b ?? 0);

  const sched1_line26 = 
    (sched1_line11 ?? 0) + 
    (sched1_line12 ?? 0) + 
    (sched1_line13 ?? 0) + 
    (sched1_line14 ?? 0) + 
    (sched1_line15 ?? 0) + 
    (sched1_line16 ?? 0) + 
    (sched1_line17 ?? 0) + 
    (sched1_line18 ?? 0) + 
    (sched1_line19a ?? 0) + 
    (sched1_line20 ?? 0) + 
    (sched1_line21 ?? 0) + 
    (sched1_line23 ?? 0) +
    (sched1_line24a ?? 0) +
    (sched1_line24b ?? 0) +
    (sched1_line24z ?? 0);
  
  const adjustments = sched1_line26;
  
  const line1_totalWages = totalW2Wages + 
    totalHouseholdWages + 
    totalTipIncome + 
    totalWaiverPayments + 
    (parseFloat(formData.disabilityPensionPreRetirement) || 0) +
    (parseFloat(formData.correctiveDistributions) || 0);

  const otherEarnedIncome = 
    (parseFloat(formData.disabilityPensionPreRetirement) || 0) +
    (parseFloat(formData.correctiveDistributions) || 0);

  const line1z = totalW2Wages + 
    totalHouseholdWages + 
    totalTipIncome + 
    totalWaiverPayments + otherEarnedIncome +
    form_8919_total_wages +
    taxableDependentCareBenefits + 
    taxableAdoptionBenefits;

  const line2b = parseFloat(formData.taxableInterest) || 0;
  const line3b = parseFloat(formData.ordinaryDividends) || 0;
  const line4b = parseFloat(formData.taxableIraDistributions) || 0;
  const line5b = parseFloat(formData.taxablePensionsAnnuities) || 0;
  const totalSSBenefits = parseFloat(formData.totalSocialSecurityBenefits) || 0;
  let calculatedTaxableSS = 0;

  if (totalSSBenefits > 0) {
    // 1. Calculate Combined Income
    const otherIncomeForSS = 
      totalW2Wages + 
      netBusinessIncome + 
      (parseFloat(formData.taxableInterest) || 0) + 
      (parseFloat(formData.taxExemptInterest) || 0) + // Important: Include tax-exempt
      (parseFloat(formData.ordinaryDividends) || 0) + 
      (parseFloat(formData.taxablePensionsAnnuities) || 0) +
      (parseFloat(formData.taxableIraDistributions) || 0) +
      sched1_line9; // Other income

    const combinedIncome = otherIncomeForSS + (totalSSBenefits * 0.5);

    // 2. Determine Base Amount
    let baseAmount = 25000; // Single, HOH, QSS, MFS (lived apart)
    if (filingStatus === 'Married Filing Jointly') baseAmount = 32000;
    if (filingStatus === 'Married Filing Separately' && !formData.livedApartEntireYear) baseAmount = 0;

    // 3. Calculate Taxable Portion (Simplified 85% rule for estimation)
    if (combinedIncome > baseAmount) {
      // This is a simplified estimation. The actual worksheet is more complex.
      // Usually it's the lesser of 85% of benefits OR 85% of the excess over threshold.
      calculatedTaxableSS = Math.min(totalSSBenefits * 0.85, (combinedIncome - baseAmount) * 0.85);
    }
  }

  // Use manual override if provided, otherwise use calculated
  const line6b = !isNaN(parseFloat(formData.taxableSocialSecurityBenefits)) 
    ? parseFloat(formData.taxableSocialSecurityBenefits) 
    : calculatedTaxableSS;
  const line7 = capitalGainsForIncome;
  const line8 = sched1_line10;
  const totalIncome_line9 = line1z + line2b + line3b + line4b + line5b + line6b + line7 + line8;
  const line10 = sched1_line26;
  const agi = totalIncome_line9 - line10;

  const grossIncome = totalIncome_line9; 
  const finalGrossIncome = grossIncome;

  // --- AUTO-CALCULATE SCHEDULE 2, LINE 17 TAXES ---
  let autoCalculatedAdditionalMedicareTax = 0;
  const totalMedicareWages = totalW2Wages + netBusinessIncome;
  const medicareTaxThreshold = 
    filingStatus === 'Married Filing Jointly' ? 250000 :
    filingStatus === 'Married Filing Separately' ? 125000 :
    200000;

  if (totalMedicareWages > medicareTaxThreshold) {
    autoCalculatedAdditionalMedicareTax = (totalMedicareWages - medicareTaxThreshold) * 0.009;
  }

  let autoCalculatedNetInvestmentIncomeTax = 0;
  const netInvestmentIncome = 
    (parseFloat(formData.taxableInterest) || 0) +
    (parseFloat(formData.ordinaryDividends) || 0) +
    (parseFloat(formData.capitalGainDistributions) || 0) +
    Math.max(0, capitalGainsForIncome) +
    netRentalIncome;

  const magi = agi;
  const niitThreshold = 
    filingStatus === 'Married Filing Jointly' ? 250000 :
    filingStatus === 'Married Filing Separately' ? 125000 :
    200000;

  const excessMAGI = Math.max(0, magi - niitThreshold);

  if (excessMAGI > 0 && netInvestmentIncome > 0) {
    const taxableAmount = Math.min(netInvestmentIncome, excessMAGI);
    autoCalculatedNetInvestmentIncomeTax = taxableAmount * 0.038;
  }

  let autoCalculatedAdditionalTaxIRA = 0;
  const earlyDistAmount = parseFloat(formData.iraEarlyDistAmount) || 0;
  const exceptionAmount = parseFloat(formData.iraExceptionAmount) || 0;

  if (earlyDistAmount > 0) {
    const taxableEarlyDist = Math.max(0, earlyDistAmount - exceptionAmount);
    autoCalculatedAdditionalTaxIRA = taxableEarlyDist * 0.10;
  }

  const sched2_line1 = parseFloat(formData.amtTax) || 0;
  const sched2_line1a = parseFloat(formData.excessAptcRepayment) || 0;
  const sched2_line1b = parseFloat(formData.repayNewCleanVehicle) || 0;
  const sched2_line1c = parseFloat(formData.repayOldCleanVehicle) || 0;
  const sched2_line1d = parseFloat(formData.recaptureEPE) || 0;
  const sched2_line1e = parseFloat(formData.excessivePaymentsEPE) || 0;
  const sched2_line1f = 0;
  const sched2_line1y = parseFloat(formData.otherAdditionsTax) || 0;
  const sched2_line1z = sched2_line1a + sched2_line1b + sched2_line1c + sched2_line1d + sched2_line1e + sched2_line1f + sched2_line1y;
  const sched2_line3 = sched2_line1z + sched2_line1;
  const sched2_line4 = selfEmploymentTax; 
  const sched2_line5 = parseFloat(formData.unreportedTipTax) || 0; 
  const sched2_line6 = parseFloat(formData.uncollectedTax) || 0;   
  const sched2_line7 = sched2_line5 + sched2_line6;
  const sched2_line8 = parseFloat(formData.taxOnIras) || 0;       
  const sched2_line9 = parseFloat(formData.householdEmploymentTax) || 0;
  const sched2_line10 = parseFloat(formData.homebuyerCreditRepayment) || 0; 
  const sched2_line11 = parseFloat(formData.additionalMedicareTax) || 0; 
  const sched2_line12 = parseFloat(formData.netInvestmentIncomeTax) || 0;
  const sched2_line13 = parseFloat(formData.uncollectedW2Tax) || 0;
  const sched2_line14 = parseFloat(formData.installmentInterestLots) || 0;
  const sched2_line15 = parseFloat(formData.installmentInterestDeferred) || 0;
  const sched2_line16 = parseFloat(formData.recaptureLowIncomeHousing) || 0;
  const sched2_line17a = parseFloat(formData.line17aAmount) || 0;
  const sched2_line17b = parseFloat(formData.taxOnTrustAccumulation) || 0;
  const sched2_line17c = parseFloat(formData.exciseTaxInsiderStock) || 0;
  const sched2_line17d = parseFloat(formData.lookBackInterest8697) || 0;
  const sched2_line17e = parseFloat(formData.lookBackInterest8866) || 0;
  const sched2_line17f = parseFloat(formData.uncollectedSocialSecurityMedicare) || 0;
  const sched2_line17g = parseFloat(formData.uncollectedSocialSecurityRRTA) || 0;
  const sched2_line17h = autoCalculatedAdditionalTaxIRA || 0;
  const sched2_line17i = parseFloat(formData.householdEmploymentTax) || 0;
  const sched2_line17j = parseFloat(formData.repaymentFirstTimeBuyer) || 0;
  const sched2_line17k = parseFloat(formData.goldenParachuteTax) || 0;
  const sched2_line17l = autoCalculatedAdditionalMedicareTax || 0;
  const sched2_line17m = autoCalculatedNetInvestmentIncomeTax || 0;
  const sched2_line17n = parseFloat(formData.section965Tax) || 0;
  const sched2_line17o = parseFloat(formData.section951aTax) || 0;
  const sched2_line17p = parseFloat(formData.section461lExcessLoss) || 0;
  const sched2_line17q = parseFloat(formData.deferredTaxSection1061) || 0;
  const sched2_line17z = parseFloat(formData.line17zAmount) || 0;
  const hsaTax = parseFloat(formData.hsaAdditionalTax) || 0;

  const sched2_line18 = sched2_line17a + sched2_line17b + sched2_line17c + 
                        sched2_line17d + sched2_line17e + sched2_line17f +
                        sched2_line17g + sched2_line17h + sched2_line17i +
                        sched2_line17j + sched2_line17k + sched2_line17l +
                        sched2_line17m + sched2_line17n + sched2_line17o +
                        sched2_line17p + sched2_line17q + sched2_line17z + hsaTax;
  const sched2_line19 = parseFloat(formData.additionalTax8812) || 0;
  const sched2_line20 = parseFloat(formData.section965TaxInstallment) || 0;
  const sched2_line21 = sched2_line4 + sched2_line7 + sched2_line8 + sched2_line9 + 
                        sched2_line10 + sched2_line11 + sched2_line12 + sched2_line13 + 
                        sched2_line14 + sched2_line15 + sched2_line16 + sched2_line18 + sched2_line19 + sched2_line20;

  // --- STANDARD DEDUCTION ---
  let calculatedStandardDeduction = statusData?.standardDeduction;

  if (filingStatus === 'Married Filing Separately' && formData?.spouseItemizes) {
    calculatedStandardDeduction = 0;
  }
  else if (formData?.isDualStatusAlien) {
    calculatedStandardDeduction = 0;
  }
  else if (formData?.canBeClaimedAsDependent) {
    const earnedIncome = totalW2Wages + netBusinessIncome;
    const limitedDeduction = Math.max(
      limits.DEPENDENT_STD_DED_BASE,
      earnedIncome + limits.DEPENDENT_EARNED_INCOME_ADD
    );
    calculatedStandardDeduction = Math.min(statusData?.standardDeduction, limitedDeduction);
  }

  if (calculatedStandardDeduction > 0) {
    const addAmount = statusData?.additionalDeductionAmount;
    if (formData?.isOver65) calculatedStandardDeduction += addAmount;
    if (formData?.isBlind) calculatedStandardDeduction += addAmount;
    if (filingStatus.includes('Married') || filingStatus === 'Qualifying Surviving Spouse') {
      if (formData?.spouseIsOver65) calculatedStandardDeduction += addAmount;
      if (formData?.spouseIsBlind) calculatedStandardDeduction += addAmount;
    }
  }

  // Helper to prefer manual input if it exists, otherwise use the itemized state
  const getVal = (manualKey, itemizedKey) => {
    const manual = parseFloat(formData[manualKey]);
    const itemized = parseFloat(formData.itemizedDeductions?.[itemizedKey]);
    // If manual is a valid number (even 0), use it. Otherwise use itemized.
    return !isNaN(manual) ? manual : (itemized || 0);
  };

  // 1. Medical
  const med = getVal('manualMedicalExpenses', 'medicalExpenses');
  const deductibleMedical = Math.max(0, med - (agi * limits.MEDICAL_EXPENSE_THRESHOLD_PERCENT));

  // 2. Taxes (SALT)
  const stateLocalIncome = getVal('manualStateTaxes', 'stateAndLocalIncomeTaxes');
  const generalSalesTax = getVal('manualGeneralSalesTax', 'generalSalesTax');
  // Determine which is higher
  const isElectingSalesTax = generalSalesTax > stateLocalIncome;
  const line5aAmount = isElectingSalesTax ? generalSalesTax : stateLocalIncome;
  const realEstate = getVal('manualRealEstateTaxes', 'realEstateTaxes');
  const personalProp = getVal('manualPersonalPropertyTaxes', 'personalPropertyTaxes');
  const totalSalt = line5aAmount + realEstate + personalProp;
  
  const saltCapLimit = filingStatus === 'Married Filing Separately' ? limits.SALT_CAP_MFS : limits.SALT_CAP;
  const deductibleSalt = Math.min(totalSalt, saltCapLimit);
  const otherTaxes = parseFloat(formData?.itemizedDeductions?.otherTaxes || 0);

  // 3. Interest
  const mortInterest = getVal('manualMortgageInterest', 'mortgageInterest');
  const mortInterestNot1098 = parseFloat(formData?.itemizedDeductions?.mortgageInterestNot1098 || 0);
  const points = parseFloat(formData?.itemizedDeductions?.pointsNot1098 || 0);
  const mortInsurance = parseFloat(formData?.itemizedDeductions?.mortgageInsurancePremiums || 0);
  const investInterest = parseFloat(formData?.itemizedDeductions?.investmentInterest || 0);
  const totalInterest = mortInterest + mortInterestNot1098 + points + mortInsurance + investInterest;

  // 4. Charity (UPDATED WITH NON-CASH LIMITS)
  
  // --- Cash (60% Limit) ---
  const rawCashCharity = getVal('manualCharityCash', 'charitableDonationsCash');
  const cashLimit = agi * 0.60;
  const deductibleCashCharity = Math.min(rawCashCharity, cashLimit);
  const newCharityCarryover = Math.max(0, rawCashCharity - cashLimit);

  // --- Non-Cash (50% Limit) ---
  const rawNonCashCharity = getVal('manualCharityNonCash', 'charitableDonationsNonCash');
  // Non-cash is generally limited to 50% of AGI (sometimes 30% for capital gain property, but 50% is the standard baseline)
  const nonCashLimit = agi * 0.50;
  
  const deductibleNonCashCharity = Math.min(rawNonCashCharity, nonCashLimit);
  const newNonCashCarryover = Math.max(0, rawNonCashCharity - nonCashLimit);

  const carryoverCharity = parseFloat(formData?.itemizedDeductions?.charitableCarryover || 0);
  
  // Total for Line 14
  const totalCharity = deductibleCashCharity + deductibleNonCashCharity + carryoverCharity;

  // 5. Casualty & Theft (Line 15)
  const casualtyInput = formData?.itemizedDeductions?.casualtyCalculator || {};
  const rawLoss = parseFloat(casualtyInput.totalLoss) || 0;
  const insurance = parseFloat(casualtyInput.insuranceReimbursement) || 0;
  
  // Step 1: Net Loss after insurance
  const netLoss = Math.max(0, rawLoss - insurance);
  
  // Step 2: Subtract $100 floor (per event)
  // (Assuming 1 event for this calculator. If 0 loss, don't subtract)
  const lossAfterFloor = netLoss > 0 ? Math.max(0, netLoss - 100) : 0;
  
  // Step 3: Subtract 10% of AGI
  const agiThreshold = agi * 0.10;
  
  // Final Deductible Amount (Only if Federally Declared)
  let deductibleCasualtyLoss = 0;
  if (casualtyInput.isFederallyDeclared) {
      deductibleCasualtyLoss = Math.max(0, lossAfterFloor - agiThreshold);
  }

  // 6. Other Itemized Deductions (Line 16) - UPDATED
  const l16 = formData?.itemizedDeductions?.line16Details || {};
  
  // A. Gambling Limit Logic
  const gamblingWinnings = parseFloat(formData.gamblingWinnings) || 0; // From Other Income
  const rawGamblingLosses = parseFloat(l16.gamblingLosses) || 0;
  // You can only deduct losses up to the amount of your winnings
  const deductibleGamblingLosses = Math.min(rawGamblingLosses, gamblingWinnings);
  
  // B. Other Specific Items
  const disasterLossL16 = parseFloat(l16.netQualifiedDisasterLoss) || 0;
  const impairment = parseFloat(l16.impairmentWorkExpenses) || 0;
  const claimRight = parseFloat(l16.claimOfRightRepayment) || 0;
  const pension = parseFloat(l16.unrecoveredPension) || 0;
  const otherMisc = parseFloat(l16.otherMiscAmount) || 0;

  // Total for Line 16
  const line16Total = deductibleGamblingLosses + disasterLossL16 + impairment + claimRight + pension + otherMisc;

  // --- FINAL TOTAL ---
  const totalItemizedDeductions = deductibleMedical + deductibleSalt + otherTaxes + totalInterest + totalCharity + deductibleCasualtyLoss + line16Total;

  // --- FINAL DEDUCTION SELECTION ---
  const forceItemized = formData?.itemizedDeductions?.forceItemized || false;
  
  // Determine if Itemized is better naturally
  const itemizedIsBetter = totalItemizedDeductions > calculatedStandardDeduction;

  // Logic: Itemize if it's better OR if the user forces it
  const deductionType = (itemizedIsBetter || forceItemized) ? 'Itemized' : 'Standard';
  
  // Logic: Use the chosen amount
  const finalDeduction = deductionType === 'Itemized' ? totalItemizedDeductions : calculatedStandardDeduction;
  
  // --- NEW: SCHEDULE 1-A CALCULATIONS ---
  const s1aLimits = yearData.LIMITS;
const isMFJ = filingStatus === 'Married Filing Jointly';

// ===========================================================================
// SCHEDULE 1-A CALCULATIONS (2025) - With _1a Suffixes
// ===========================================================================


// --- Part I: MAGI Calculation ---
const foreignIncomeAddbacks = 
  (parseFloat(formData.puertoRicoIncome) || 0) +            // Line 2a
  (parseFloat(formData.foreignEarnedIncomeExclusion) || 0) + // Line 2b
  (parseFloat(formData.foreignHousingExclusion) || 0) +      // Line 2c
  (parseFloat(formData.possessionExclusion) || 0);           // Line 2d

const magi_s1a = agi + foreignIncomeAddbacks; // Line 3

const sched1A = {
  tips: 0,
  overtime: 0,
  carLoan: 0,
  seniorBonus: 0,
  total: 0,
  tipsCalc: {},
  overtimeCalc: {},
  carLoanCalc: {},
  seniorCalc: {}
};

// --- Part II: No Tax on Tips ---
const tipsW2 = parseFloat(formData.tipsW2) || 0;             // Line 4a
const tips4137 = parseFloat(formData.tips4137) || 0;         // Line 4b
const tipsSelf = parseFloat(formData.tipsSelfEmployed) || 0; // Line 5

// Line 4c: Enter the larger of 4a or 4b
const line4c_1a = Math.max(tipsW2, tips4137);

// Line 6: Add lines 4c and 5
const line6_1a = line4c_1a + tipsSelf;

// Line 7: Cap at $25,000
const line7_1a = Math.min(line6_1a, s1aLimits.TIPS_CAP); 

// --- TIPS PHASE-OUT (Lines 8-13) ---
const line8_1a = magi_s1a; // MAGI
const line9_1a = isMFJ ? s1aLimits.TIPS_THRESHOLD_MFJ : s1aLimits.TIPS_THRESHOLD_SINGLE;

let line10_1a = 0; // Excess
let line11_1a = 0; // Steps
let line12_1a = 0; // Reduction
let line13_1a = 0; // Final Deduction

if (line8_1a > line9_1a) {
    line10_1a = line8_1a - line9_1a;
    line11_1a = Math.floor(line10_1a / s1aLimits.TIPS_PHASEOUT_STEP);
    line12_1a = line11_1a * s1aLimits.TIPS_REDUCTION_AMOUNT;
    line13_1a = Math.max(0, line7_1a - line12_1a);
} else {
    line13_1a = line7_1a;
}

sched1A.tips = line13_1a;
sched1A.tipsCalc = { line4c_1a, line6_1a, line7_1a, line8_1a, line9_1a, line10_1a, line11_1a, line12_1a, line13_1a };

// --- Part III: No Tax on Overtime ---
const otW2 = parseFloat(formData.overtimeW2) || 0;     // Line 14a
const ot1099 = parseFloat(formData.overtime1099) || 0; // Line 14b

const line14a_1a = otW2;
const line14b_1a = ot1099;

// Line 14c
const line14c_1a = otW2 + ot1099;

// Line 15: Cap
const overtimeMax = isMFJ ? s1aLimits.OVERTIME_CAP_MFJ : s1aLimits.OVERTIME_CAP_SINGLE;
const line15_1a = Math.min(line14c_1a, overtimeMax); 

// --- OVERTIME PHASE-OUT (Lines 16-21) ---
const line16_1a = magi_s1a; // MAGI
const line17_1a = isMFJ ? s1aLimits.OVERTIME_THRESHOLD_MFJ : s1aLimits.OVERTIME_THRESHOLD_SINGLE;

let line18_1a = 0; // Excess
let line19_1a = 0; // Steps
let line20_1a = 0; // Reduction
let line21_1a = 0; // Final Deduction

if (line16_1a > line17_1a) {
    line18_1a = line16_1a - line17_1a;
    line19_1a = Math.floor(line18_1a / s1aLimits.OVERTIME_PHASEOUT_STEP);
    line20_1a = line19_1a * s1aLimits.OVERTIME_REDUCTION_AMOUNT;
    line21_1a = Math.max(0, line15_1a - line20_1a);
} else {
    line21_1a = line15_1a;
}

sched1A.overtime = line21_1a;
sched1A.overtimeCalc = { line14a_1a, line14b_1a, line14c_1a, line15_1a, line16_1a, line17_1a, line18_1a, line19_1a, line20_1a, line21_1a };

// Vehicle 1 Net
const car1_total = parseFloat(formData.car1_totalInterest) || 0;
const car1_biz = parseFloat(formData.car1_businessDeduction) || 0;
const car1_net = Math.max(0, car1_total - car1_biz); // Line 22a Col(iii)

// Vehicle 2 Net
const car2_total = parseFloat(formData.car2_totalInterest) || 0;
const car2_biz = parseFloat(formData.car2_businessDeduction) || 0;
const car2_net = Math.max(0, car2_total - car2_biz); // Line 22b Col(iii)

// Line 23: Total Net Interest
const line23_1a = car1_net + car2_net;

// Line 24: Cap ($10k)
const line24_1a = Math.min(line23_1a, s1aLimits.CAR_LOAN_CAP);

// --- CAR LOAN PHASE-OUT (Lines 25-30) ---
const line25_1a = magi_s1a; // MAGI
const line26_1a = isMFJ ? s1aLimits.CAR_LOAN_THRESHOLD_MFJ : s1aLimits.CAR_LOAN_THRESHOLD_SINGLE;

let line27_1a = 0; // Excess
let line28_1a = 0; // Steps
let line29_1a = 0; // Reduction
let line30_1a = 0; // Final Deduction

if (line25_1a > line26_1a) {
    line27_1a = line25_1a - line26_1a;
    // Note: Car loan uses Math.ceil (Step UP)
    line28_1a = Math.ceil(line27_1a / s1aLimits.CAR_LOAN_PHASEOUT_STEP);
    line29_1a = line28_1a * s1aLimits.CAR_LOAN_REDUCTION_AMOUNT; // $200 multiplier
    line30_1a = Math.max(0, line24_1a - line29_1a);
} else {
    line30_1a = line24_1a;
}

sched1A.carLoanCalc = {
    car1: { vin: formData?.car1_vin, biz: car1_biz, net: car1_net },
    car2: { vin: formData?.car2_vin, biz: car2_biz, net: car2_net },
    line23_1a,
    line24_1a,
    // ... include phase-out lines (25-30) ...
    line25_1a, line26_1a, line27_1a, line28_1a, line29_1a, line30_1a
};

sched1A.carLoan = line30_1a;
sched1A.carLoanCalc = { line23_1a, line24_1a, line25_1a, line26_1a, line27_1a, line28_1a, line29_1a, line30_1a };

// --- Part V: Enhanced Deduction for Seniors ---
const line31_1a = magi_s1a; // MAGI
const line32_1a = isMFJ ? s1aLimits.SENIOR_THRESHOLD_MFJ : s1aLimits.SENIOR_THRESHOLD_SINGLE;

let line33_1a = 0; // Excess
let line34_1a = 0; // Reduction Amount
let line35_1a = s1aLimits.SENIOR_BONUS_BASE; // $6,000 Base

if (line31_1a > line32_1a) {
    line33_1a = line31_1a - line32_1a;
    line34_1a = line33_1a * s1aLimits.SENIOR_PHASEOUT_RATE; // 6%
    line35_1a = Math.max(0, line35_1a - line34_1a);
}

// Apply to eligible persons (Line 36a/36b)
let line37_1a = 0;
if (formData?.isOver65) line37_1a += line35_1a;
if (isMFJ && formData?.spouseIsOver65) line37_1a += line35_1a;

sched1A.seniorBonus = line37_1a;
sched1A.seniorCalc = { line31_1a, line32_1a, line33_1a, line34_1a, line35_1a, line37_1a };

// --- Part VI: Total ---
// Line 38: Add lines 13, 21, 30, and 37
const line38_1a = sched1A.tips + sched1A.overtime + sched1A.carLoan + sched1A.seniorBonus;
sched1A.total = line38_1a;
// --- UPDATED FORM 1040 LINES ---
const line12 = finalDeduction; // Standard or Itemized
let line13 = 0; // QBI Deduction
const manualQBI = parseFloat(formData.manualQBIDeduction);
// FIX: Check !isNaN to safely handle undefined/empty values
  if (!isNaN(manualQBI)) {
    line13 = manualQBI;
  } else if (netBusinessIncome > 0) {
    const qbiAdjustments = 
      sched1_line15 +
      (parseFloat(formData.selfEmployedHealthInsurance) || 0) +
      (parseFloat(formData.selfEmployedRetirementDeduction) || 0);
    
    const qualifiedBusinessIncome = Math.max(0, netBusinessIncome - qbiAdjustments);
    const tentativeQBIDeduction = qualifiedBusinessIncome * 0.20;

    const taxableIncomeBeforeQBI = Math.max(0, agi - line12);
    const netCapitalGains = (parseFloat(formData.qualifiedDividends) || 0) + Math.max(0, capitalGainsForIncome);
    
    const incomeLimitationBase = Math.max(0, taxableIncomeBeforeQBI - netCapitalGains);
    const incomeLimitation = incomeLimitationBase * 0.20;

    line13 = Math.min(tentativeQBIDeduction, incomeLimitation);
  }
const line13a = line13; // QBI Deduction (renamed from line13 to 13a)
const line13b = sched1A.total; // NEW: Schedule 1-A Total

// Updated Line 14
const line14 = line12 + line13a + line13b;

// Updated Taxable Income
const taxableIncome = Math.max(0, agi - line14);

  const line15 = taxableIncome;

   // ===========================================================================
  // SCHEDULE D & TAX CALCULATION
  // ===========================================================================

  // 1. Separate Transactions into Buckets
  // Added 'adjustments' property to each bucket
  let st_A = { proceeds: 0, cost: 0, adjustments: 0, gain: 0 }; 
  let st_B = { proceeds: 0, cost: 0, adjustments: 0, gain: 0 }; 
  let st_C = { proceeds: 0, cost: 0, adjustments: 0, gain: 0 }; 

  let lt_D = { proceeds: 0, cost: 0, adjustments: 0, gain: 0 }; 
  let lt_E = { proceeds: 0, cost: 0, adjustments: 0, gain: 0 }; 
  let lt_F = { proceeds: 0, cost: 0, adjustments: 0, gain: 0 }; 

  investmentsData.forEach(item => {
    const proceeds = parseFloat(item.saleProceeds || 0);
    const cost = parseFloat(item.costBasis || 0);
    const wash = parseFloat(item.washSaleAdjustment || 0); // This is the adjustment
    const gain = proceeds - cost + wash;

    // Determine holding period
    let isLongTerm = false;
    if (item.purchaseDate && item.saleDate) {
        const start = new Date(item.purchaseDate);
        const end = new Date(item.saleDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
        if (diffDays > 365) isLongTerm = true;
    }

    // Determine Reporting Status Bucket
    // 'Basis Reported' -> A/D
    // 'Basis Not Reported' -> B/E
    // 'Not on 1099-B' -> C/F
    const status = item.reportingStatus || 'Basis Reported';

    // Helper to add to bucket
    const addTo = (bucket) => {
        bucket.proceeds += proceeds;
        bucket.cost += cost;
        bucket.adjustments += wash; // <--- Accumulate Adjustments
        bucket.gain += gain;
    };

    if (isLongTerm) {
        if (status === 'Basis Reported') addTo(lt_D);
        else if (status === 'Basis Not Reported') addTo(lt_E);
        else addTo(lt_F);
    } else {
        if (status === 'Basis Reported') addTo(st_A);
        else if (status === 'Basis Not Reported') addTo(st_B);
        else addTo(st_C);
    }
  });

  // 2. Calculate Schedule D Lines
  const st_carryover = parseFloat(formData.capitalLossCarryoverShort) || 0;
  const lt_carryover = parseFloat(formData.capitalLossCarryoverLong) || 0;
  
  // Short-Term Total
  const schedD_Line7 = 
      st_A.gain + st_B.gain + st_C.gain + 
      (parseFloat(formData.schedD_line4_shortTermFormGains) || 0) + 
      (parseFloat(formData.schedD_line5_shortTermK1Gains) || 0) - 
      st_carryover;

  // Long-Term Total
  const schedD_Line15 = 
      lt_D.gain + lt_E.gain + lt_F.gain + 
      (parseFloat(formData.schedD_line11_longTermFormGains) || 0) + 
      (parseFloat(formData.schedD_line12_longTermK1Gains) || 0) + 
      (parseFloat(formData.capitalGainDistributions) || 0) + 
        (parseFloat(formData.childCapitalGainAmount) || 0) - 
      lt_carryover;

  const schedD_Line16 = schedD_Line7 + schedD_Line15;

  // 3. Determine Allowable Loss (Line 21)
  let allowableLoss = 0;
  if (schedD_Line16 < 0) {
      const limit = filingStatus === 'Married Filing Separately' ? 1500 : 3000;
      allowableLoss = Math.min(Math.abs(schedD_Line16), limit);
  }

  // 4. Calculate Tax
  let finalTax = 0;
  const qualifiedDivs = parseFloat(formData.qualifiedDividends) || 0;
  
  // Check if we need the Schedule D Tax Worksheet
  // Condition: (Line 15 > 0 AND Line 16 > 0) OR (Qualified Dividends > 0)
  const hasCapGains = (schedD_Line15 > 0 && schedD_Line16 > 0) || (qualifiedDivs > 0);

  if (hasCapGains && taxableIncome > 0) {
      // --- SCHEDULE D TAX WORKSHEET LOGIC ---
      
      // Line 1: Taxable Income
      const w_line1 = taxableIncome;
      
      // Line 2: Qualified Dividends
      const w_line2 = qualifiedDivs;
      
      // Line 3: Long-Term Capital Gain (Line 15 or 16, whichever is smaller)
      // Note: If either is loss/zero, this part is 0 for the worksheet usually, 
      // but strictly it's min(15, 16) if both > 0.
      let w_line3 = 0;
      if (schedD_Line15 > 0 && schedD_Line16 > 0) {
          w_line3 = Math.min(schedD_Line15, schedD_Line16);
      }

      // Line 4: Add Line 2 + Line 3
      const w_line4 = w_line2 + w_line3;

      // Line 5: Net Capital Gain (Line 4)
      // (Skipping complex 28% / 1250 logic for brevity, assuming 0 for now)
      const w_line5 = w_line4; 

      // Line 6: Subtract Line 5 from Line 1 (Ordinary Income)
      const w_line6 = Math.max(0, w_line1 - w_line5);

      // Line 7: Tax on Line 6 (Ordinary Income Tax)
      // Use standard brackets on the ordinary portion
      let taxOnOrdinary = 0;
      let remainingOrd = w_line6;
      let lastLimit = 0;
      for (const bracket of statusData?.brackets) {
        if (remainingOrd <= 0) break;
        const taxableInBracket = Math.min(remainingOrd, bracket.upTo - lastLimit);
        taxOnOrdinary += taxableInBracket * bracket.rate;
        remainingOrd -= taxableInBracket;
        lastLimit = bracket.upTo;
      }
      if (remainingOrd > 0) {
         taxOnOrdinary += remainingOrd * statusData?.brackets[statusData?.brackets.length - 1].rate;
      }
      const w_line7 = taxOnOrdinary;

      // --- 0% Rate Calculation ---
      // Line 8: Enter limit for 0% rate
      const limit0Percent = filingStatus === 'Married Filing Jointly' ? 94050 : 
                            filingStatus === 'Head of Household' ? 63000 : 47025; // 2024 est
      const w_line8 = limit0Percent;

      // Line 9: Enter Line 1 or Line 6 (smaller)
      const w_line9 = Math.min(w_line1, w_line6);

      // Line 10: Subtract Line 9 from Line 8
      const w_line10 = Math.max(0, w_line8 - w_line9);

      // Line 11: Enter smaller of Line 1 or Line 5
      const w_line11 = Math.min(w_line1, w_line5);

      // Line 12: Enter smaller of Line 10 or Line 11 (Amount taxed at 0%)
      const w_line12 = Math.min(w_line10, w_line11);

      // --- 15% Rate Calculation ---
      // Line 13: Subtract Line 12 from Line 11
      const w_line13 = w_line11 - w_line12;

      // Line 14: Enter limit for 15% rate
      const limit15Percent = filingStatus === 'Married Filing Jointly' ? 583750 : 
                             filingStatus === 'Head of Household' ? 551350 : 518900; // 2024 est
      const w_line14 = limit15Percent;

      // Line 15: Enter smaller of Line 1 or Line 6
      const w_line15 = Math.min(w_line1, w_line6);

      // Line 16: Add Line 12 and Line 15
      const w_line16 = w_line12 + w_line15;

      // Line 17: Subtract Line 16 from Line 14
      const w_line17 = Math.max(0, w_line14 - w_line16);

      // Line 18: Enter smaller of Line 13 or Line 17 (Amount taxed at 15%)
      const w_line18 = Math.min(w_line13, w_line17);

      // Line 19: Multiply Line 18 by 15%
      const w_line19 = w_line18 * 0.15;

      // --- 20% Rate Calculation ---
      // Line 20: Add Line 12 and Line 18
      const w_line20 = w_line12 + w_line18;

      // Line 21: Subtract Line 20 from Line 11 (Amount taxed at 20%)
      const w_line21 = w_line11 - w_line20;

      // Line 22: Multiply Line 21 by 20%
      const w_line22 = w_line21 * 0.20;

      // --- Final Sum ---
      // Line 23: Tax on all taxable income (Line 7 + Line 19 + Line 22)
      const w_line23 = w_line7 + w_line19 + w_line22;

      // Line 24: Tax using standard tables on full amount (Line 1)
      // (We need to calculate what tax WOULD be without preferential rates to ensure we don't pay MORE)
      let taxStandard = 0;
      let remStd = w_line1;
      let lastLimStd = 0;
      for (const bracket of statusData?.brackets) {
        if (remStd <= 0) break;
        const taxableInBracket = Math.min(remStd, bracket.upTo - lastLimStd);
        taxStandard += taxableInBracket * bracket.rate;
        remStd -= taxableInBracket;
        lastLimStd = bracket.upTo;
      }
      if (remStd > 0) taxStandard += remStd * statusData?.brackets[statusData?.brackets.length - 1].rate;

      // Line 25: Smaller of Line 23 or Line 24
      finalTax = Math.min(w_line23, taxStandard);

  } else {
      // --- STANDARD TAX CALCULATION (No Capital Gains) ---
      let remainingOrdIncome = taxableIncome;
      let lastLimit = 0;
      for (const bracket of statusData?.brackets) {
        if (remainingOrdIncome <= 0) break;
        const taxableInBracket = Math.min(remainingOrdIncome, bracket.upTo - lastLimit);
        finalTax += taxableInBracket * bracket.rate;
        remainingOrdIncome -= taxableInBracket;
        lastLimit = bracket.upTo;
      }
      if (remainingOrdIncome > 0) {
        finalTax += remainingOrdIncome * statusData?.brackets[statusData?.brackets.length - 1].rate;
      }
  }

    // Add other taxes (Form 8814, 4972, and Box 3 items)
  const taxForm8814 = parseFloat(formData.taxForm8814Amount) || 0;
  const taxForm4972 = parseFloat(formData.taxForm4972Amount) || 0;
  
  // Box 3 Items
  const tax962 = parseFloat(formData.taxSection962) || 0;
  const taxECR = parseFloat(formData.taxRecaptureEducation) || 0;
  const tax8621 = parseFloat(formData.taxForm8621) || 0;
  const tax8978 = parseFloat(formData.taxForm8978) || 0;
  const tax965i = parseFloat(formData.taxSection965i) || 0;
  const taxOther = parseFloat(formData.otherTaxFormAmount) || 0;

  const line16 = finalTax + taxForm8814 + taxForm4972 + tax962 + taxECR + tax8621 + tax8978 + tax965i + taxOther;
  const line17 = sched2_line3;  
  const line18 = line16 + line17;

  // --- CREDITS CALCULATION (IN CORRECT ORDER) ---
  const dependents = formData?.dependents || [];
  let totalCTC = 0;
  let totalODC = 0;
  dependents.forEach(dep => {
    const quals = checkQualifications(dep, taxYear);
    if (quals.isQualifyingChildForCTC) {
      totalCTC += limits.CTC_AMOUNT;
    } else if (quals.isQualifyingRelativeForODC) {
      totalODC += limits.ODC_AMOUNT;
    }
  });

  const line19 = totalCTC + totalODC;

  // B. Dependent Care Credit
  let dependentCareCredit = 0;
  const careData = formData?.dependentCare || {};
  const numberOfQualifyingPersons = dependents.filter(dep => checkQualifications(dep, taxYear).isQualifyingPersonForCare).length;
  let isQualifiedForCareCredit = careData.wasForWorkOrJobSearch && careData.providerWasNotSpouseOrDependent;
  
  if (filingStatus === 'Married Filing Separately') {
    isQualifiedForCareCredit = isQualifiedForCareCredit && careData.livedApartFromSpouse && careData.homeWasQualifyingPersonsMainHome && careData.paidMoreThanHalfCostOfHome;
  }

  if (isQualifiedForCareCredit && numberOfQualifyingPersons > 0) {
    const calculatedUserEarnedIncome = totalW2Wages + netBusinessIncome;
    const inputUserEarnedIncome = parseFloat(careData.userEarnedIncome) || 0;
    const inputSpouseEarnedIncome = parseFloat(careData.spouseEarnedIncome) || 0;
    const baseUserEarnedIncome = inputUserEarnedIncome > 0 ? inputUserEarnedIncome : calculatedUserEarnedIncome;

    const monthlyDeemedIncome = numberOfQualifyingPersons >= 2 ? 500 : 250;
    const yearlyDeemedIncome = monthlyDeemedIncome * 12;

    let finalUserEarnedIncome = baseUserEarnedIncome;
    if (careData.userWasStudentOrDisabled) {
      finalUserEarnedIncome = Math.max(baseUserEarnedIncome, yearlyDeemedIncome);
    }

    let finalSpouseEarnedIncome = inputSpouseEarnedIncome;
    if (careData.spouseWasStudentOrDisabled) {
      finalSpouseEarnedIncome = Math.max(inputSpouseEarnedIncome, yearlyDeemedIncome);
    }

    const earnedIncomeLimit = (filingStatus === 'Married Filing Jointly') 
      ? Math.min(finalUserEarnedIncome, finalSpouseEarnedIncome) 
      : finalUserEarnedIncome;
      
    const expenseLimit = numberOfQualifyingPersons >= 2 
      ? limits.DEPENDENT_CARE_LIMIT_2 
      : limits.DEPENDENT_CARE_LIMIT_1;
    const actualExpenses = careProviders.reduce((sum, provider) => sum + (parseFloat(provider.amountPaid) || 0), 0);
    const creditBaseAmount = Math.min(actualExpenses, expenseLimit, earnedIncomeLimit);
    
    let creditRate = 0.20;
    if (agi <= 15000) {
      creditRate = 0.35;
    } else if (agi <= 43000) {
      const agiOver = agi - 15000;
      const reductions = Math.floor(agiOver / 2000);
      creditRate = Math.max(0.20, 0.35 - (reductions * 0.01));
    }
    dependentCareCredit = creditBaseAmount * creditRate;
  }

  // C. Saver's Credit
  let saversCredit = 0;
  const contribution = Math.min(parseFloat(formData.retirementContributions || 0), 2000);
  if (agi > 0 && contribution > 0 && statusData?.saversCreditThresholds) {
    for (const threshold of statusData?.saversCreditThresholds) {
      if (agi <= threshold.upTo) {
        saversCredit = contribution * threshold.rate;
        break;
      }
    }
  }

  // E. Education Credits (Non-Refundable)
  // FIX: Define this BEFORE Adoption Credit so it can be used in the limit calculation
  const educationCredit = parseFloat(formData.educationCreditNonRefundable) || 0;

  // D. Adoption Credit (MUST BE CALCULATED BEFORE SCHEDULE 3)
  const adoptionLine1 = parseFloat(formData.adoptionCredit?.form8839_year2024_line14) || 0;
  const adoptionLine2 = line18;
  // Now 'educationCredit' is defined and valid (0 if empty), preventing NaN
  const adoptionLine3 = line19 + dependentCareCredit + saversCredit + educationCredit;
  const adoptionLine4 = Math.max(0, adoptionLine2 - adoptionLine3);
  const adoptionCreditAllowed = Math.min(adoptionLine1, adoptionLine4);

  const adoptionWorksheet = {
    line1: adoptionLine1,
    line2: adoptionLine2,
    line3: adoptionLine3,
    line4: adoptionLine4,
    line5: adoptionCreditAllowed
  };

  // NOW CALCULATE SCHEDULE 3 (ALL DEPENDENCIES ARE READY)
  const sched3_line1 = parseFloat(formData.foreignTaxCredit) || 0;
  const sched3_line2 = dependentCareCredit;
  const sched3_line3 = educationCredit; // Use the variable we defined above
  const sched3_line4 = saversCredit;
  
  const sched3_line5a = parseFloat(formData.residentialCleanEnergyCredit) || 0;
  const sched3_line5b = parseFloat(formData.energyEfficientHomeCredit) || 0;
  
  const sched3_line6a = parseFloat(formData.generalBusinessCredit) || 0;
  const sched3_line6b = parseFloat(formData.priorYearMinTaxCredit) || 0;
  const sched3_line6c = adoptionCreditAllowed;
  const sched3_line6d = parseFloat(formData.elderlyDisabledCredit) || 0;
  const sched3_line6e = 0;
  const sched3_line6f = parseFloat(formData.cleanVehicleCredit) || 0;
  const sched3_line6g = parseFloat(formData.mortgageInterestCredit) || 0;
  const sched3_line6h = parseFloat(formData.dcHomebuyerCredit) || 0;
  const sched3_line6i = parseFloat(formData.qualifiedElectricVehicleCredit) || 0;
  const sched3_line6j = parseFloat(formData.alternativeFuelVehicleCredit) || 0;
  const sched3_line6k = parseFloat(formData.taxCreditBonds) || 0;
  const sched3_line6l = parseFloat(formData.form8978Credit) || 0;
  const sched3_line6m = parseFloat(formData.previouslyOwnedCleanVehicle) || 0;
  const sched3_line6z = parseFloat(formData.otherNonrefundableCredits) || 0;

  const sched3_line7 = sched3_line6a + sched3_line6b + sched3_line6c + 
                       sched3_line6d + sched3_line6e + sched3_line6f + 
                       sched3_line6g + sched3_line6h + sched3_line6i + 
                       sched3_line6j + sched3_line6k + sched3_line6l + 
                       sched3_line6m + sched3_line6z;
  
  const sched3_line8 = sched3_line1 + sched3_line2 + sched3_line3 + 
                       sched3_line4 + sched3_line5a + sched3_line5b + sched3_line7;

  const sched3_line9 = parseFloat(formData.netPremiumTaxCredit) || 0;
  const sched3_line10 = parseFloat(formData.amountPaidWithExtension) || 0;
  const sched3_line11 = parseFloat(formData.excessSocialSecurityTax) || 0;
  const sched3_line12 = parseFloat(formData.fuelTaxCredit) || 0;
  
  const sched3_line13a = parseFloat(formData.form2439Payment) || 0;
  const sched3_line13b = parseFloat(formData.section1341Credit) || 0;
  const sched3_line13c = parseFloat(formData.electivePaymentElection) || 0;
  const sched3_line13d = parseFloat(formData.deferred965Liability) || 0;
  const sched3_line13z = parseFloat(formData.otherRefundableCredits) || 0;
  
  const sched3_line14 = sched3_line13a + sched3_line13b + sched3_line13c + 
                        sched3_line13d + sched3_line13z;
  
  const sched3_line15 = sched3_line9 + sched3_line10 + sched3_line11 + 
                        sched3_line12 + sched3_line14;

  const line20 = sched3_line8;
  const line21 = line19 + line20;
  const line22 = Math.max(0, line18 - line21);
  const line23 = sched2_line21;
  const line24 = line22 + line23;

  const totalCredits = line21;
  const taxLiability = line24;
  const taxesWithheld = w2Data.reduce((sum, w2) => sum + parseFloat(w2.federalTaxWithheld || 0), 0);

  // --- PAYMENTS ---
  const line25a = w2Data.reduce((sum, w2) => sum + (parseFloat(w2.federalTaxWithheld) || 0), 0);

  const line25b = incomeData.reduce((sum, item) => {
    const amount = parseFloat(item.amount) || 0;
    const rate = parseFloat(item.federalTax) || 0;
    return sum + (amount * (rate / 100));
  }, 0);

  const withholding_unemployment = parseFloat(formData.unemploymentWithholding) || 0;
  const withholding_social_security = parseFloat(formData.socialSecurityWithholding) || 0;
  const withholding_retirement = parseFloat(formData.retirementWithholding) || 0;
  const withholding_other_manual = parseFloat(formData.otherFederalWithholding) || 0;

  const line25c = withholding_unemployment + withholding_social_security + withholding_retirement + withholding_other_manual;
  const line25d = line25a + line25b + line25c;
  const line26 = parseFloat(formData.estimatedTaxPayments) || 0;

  const investmentIncome = (parseFloat(formData.taxableInterest) || 0) + 
                           (parseFloat(formData.ordinaryDividends) || 0) + 
                           (parseFloat(formData.capitalGainDistributions) || 0) +
                           Math.max(0, capitalGainsForIncome);

  const line27 = parseFloat(formData.earnedIncomeCredit) || 0;

  // Line 28: Additional Child Tax Credit
  let line28 = 0;
  const manualACTC = parseFloat(formData.manualAdditionalChildTaxCredit);

  // FIX: Check !isNaN to safely handle undefined/empty values
  if (!isNaN(manualACTC)) {
     line28 = manualACTC;
  } else {
     const qualifyingChildrenCount = dependents.filter(dep => checkQualifications(dep, taxYear).isQualifyingChildForCTC).length;
     const totalPotentialCTC = qualifyingChildrenCount * limits.CTC_AMOUNT;
     const ctcUsedNonRefundable = Math.min(totalPotentialCTC, line18);
     const unusedCTC = Math.max(0, totalPotentialCTC - ctcUsedNonRefundable);
     
     if (unusedCTC > 0) {
       const earnedIncomeForACTC = totalW2Wages + netBusinessIncome;
       const earnedIncomeThreshold = 2500;
       const calculatedACTC = Math.max(0, (earnedIncomeForACTC - earnedIncomeThreshold) * 0.15);
       
       const refundableCapPerChild = limits.ACTC_REFUNDABLE_CAP; 
       const maxRefundable = qualifyingChildrenCount * refundableCapPerChild;

       line28 = Math.min(unusedCTC, calculatedACTC, maxRefundable);
     }
  }

  // Line 29: American Opportunity Credit (Refundable)
  const line29 = parseFloat(formData.refundableEducationCredit) || 0;

  // Line 30: Reserved (0)
  const line30 = 0;

  // Line 31: Net Premium Tax Credit
  const line31 = parseFloat(formData.netPremiumTaxCredit) || 0;

  // Adoption Credit Logic
const maxAdoptionCredit = 17280;
const adoptionExpenses = parseFloat(formData.adoptionExpenses) || 0;
const allowedAdoption = Math.min(adoptionExpenses, maxAdoptionCredit);

// Split into Refundable vs Non-Refundable
// 2025 Rule: Up to $5,000 is refundable
const refundableAdoption = Math.min(allowedAdoption, 5000);
const nonRefundableAdoption = Math.max(0, allowedAdoption - 5000);

// Add refundable portion to Line 32 (Total Refundable Credits)
const line32 = line27 + line28 + line29 + line30 + line31 + refundableAdoption;
  const line33 = line25d + line26 + line32;

  // --- PENALTY & REFUND/OWE ---
  let line38 = 0;
  const amountOwedBeforeEst = Math.max(0, line24 - line25d);
  
  // Only calculate penalty if they owe > $1000 AND we have prior year info
  // If priorYearTax is 0 (default), we assume they didn't file or had 0 tax, 
  // but to be safe/less annoying, we can skip penalty calc if priorYearTax is missing/0 in this simple app.
  const hasPriorYearInfo = (parseFloat(formData.priorYearTax) || 0) > 0;
  
  if (amountOwedBeforeEst > 1000 && hasPriorYearInfo) {
    const thresholdCurrent = line24 * 0.90;
    const priorYearTax = parseFloat(formData.priorYearTax) || 0;
    const isHighIncome = agi > 150000; 
    const priorYearPercentage = isHighIncome ? 1.10 : 1.00;
    const thresholdPrior = priorYearTax * priorYearPercentage;

    const requiredAnnualPayment = Math.min(thresholdCurrent, thresholdPrior);
    const totalPaymentsMade = line25d + line26;

    if (totalPaymentsMade < requiredAnnualPayment) {
      const shortfall = requiredAnnualPayment - totalPaymentsMade;
      line38 = shortfall * 0.06; // Updated to ~6% (approximate IRS rate)
    }
  }
  const isSmallBalance = amountOwedBeforeEst < 1000;

  if (!isSmallBalance) {
    const thresholdCurrent = line24 * 0.90;
    const priorYearTax = parseFloat(formData.priorYearTax) || 0;
    const isHighIncome = agi > 150000; 
    const priorYearPercentage = isHighIncome ? 1.10 : 1.00;
    const thresholdPrior = priorYearTax * priorYearPercentage;

    const requiredAnnualPayment = (priorYearTax > 0) 
      ? Math.min(thresholdCurrent, thresholdPrior) 
      : thresholdCurrent;

    const totalPaymentsMade = line25d + line26;

    if (totalPaymentsMade < requiredAnnualPayment) {
      const shortfall = requiredAnnualPayment - totalPaymentsMade;
      line38 = shortfall * 0.08; 
    }
  }

  let line34 = 0;
  let line35a = 0;
  let line36 = 0;
  let line37 = 0;

  if (line33 > line24) {
    line34 = line33 - line24;
    const requestedApplication = parseFloat(formData.amountAppliedToNextYear) || 0;
    line36 = Math.min(line34, requestedApplication);

    let remainingOverpayment = line34 - line36;
    remainingOverpayment -= line38;

    if (remainingOverpayment >= 0) {
      line35a = remainingOverpayment;
      line37 = 0; 
    } else {
      line35a = 0;
      line37 = Math.abs(remainingOverpayment);
      line34 = 0;
      line36 = 0;
    }
  } else {
    const baseTaxOwed = line24 - line33;
    line37 = baseTaxOwed + line38;
    line34 = 0;
    line35a = 0;
    line36 = 0;
  }

  const line35b = formData?.refundRoutingNumber || '';
  const line35c = formData?.refundAccountType || 'Checking';
  const line35d = formData?.refundAccountNumber || '';
  const finalTaxOwed = line37 > 0 ? line37 : (line35a * -1);

  // State Tax Calculation
  const stateConstants = yearData.CA[filingStatus]; 
  const stateAGI = agi;
  const stateDeduction = stateConstants.standardDeduction;
  const stateTaxableIncome = Math.max(0, stateAGI - stateDeduction);
  let stateTaxLiability = 0;
  let remainingStateIncome = stateTaxableIncome;
  let lastStateLimit = 0;
  for (const bracket of stateConstants.brackets) {
    if (remainingStateIncome <= 0) break;
    const taxableInBracket = Math.min(remainingStateIncome, bracket.upTo - lastStateLimit);
    stateTaxLiability += taxableInBracket * bracket.rate;
    remainingStateIncome -= taxableInBracket;
    lastStateLimit = bracket.upTo;
  }
  if (remainingStateIncome > 0) {
    const finalBracketRate = stateConstants.brackets[stateConstants.brackets.length - 1].rate;
    stateTaxLiability += remainingStateIncome * finalBracketRate;
  }
  const finalStateTaxOwed = stateTaxLiability;

  return {
    grossIncome: finalGrossIncome, 
    agi, 
    deductionType, 
    taxableIncome, 
    taxLiability, 
    taxesWithheld, 
    totalCredits, 
    finalTaxOwed, 
    totalW2Wages,
    netBusinessIncome,
    stateTaxableIncome, 
    stateTaxLiability, 
    totalHouseholdWages, 
    totalTipIncome, 
    totalWaiverPayments, 
    finalStateTaxOwed, 
    form_8919_total_wages,
    totalItemizedDeductions, 
    adjustments, 
    finalDeduction, 
    newCapitalLossCarryover, 
    line1z,
    line2b,
    line3b,
    line4b,
    line5b,
    line6b,
    line7,
    line8,
    totalIncome_line9,
    line10,
    line12,
    line13,
    line14,
    line15,
    line16,
    line17,
    line18,
    line19,
    line20,
    line21,
    line22,
    line23,
    line24,
    line25a,
    line25b,
    line25c,
    line25d,
    line26,
    line27,
    line28,
    line29,
    line32,
    line33,
    line34,
    line35a,
    line35b,
    line35c,
    line35d,
    line36,
    line37,
    line38,
    otherEarnedIncome,
    adoptionWorksheet,
    isScheduleDRequired,
    sched1_line1,
    sched1_line2a,
    sched1_line3,
    sched1_line4,
    sched1_line5,
    sched1_line6,
    sched1_line7,
    sched1_line8z,
    sched1_line9,
    sched1_line10,
    sched1_line11,
    sched1_line12,
    sched1_line13,
    sched1_line14,
    sched1_line15,
    sched1_line16,
    sched1_line17,
    sched1_line18,
    sched1_line19a,
    sched1_line20,
    sched1_line21,
    sched1_line23,
    sched1_line24a,
    sched1_line24b,
    sched1_line24z,
    sched1_line26,
    sched1_line8s,
    saleOfPersonalItems1099K,
    totalAdjustmentsSched1,
    sched2_line1,
    sched2_line1a,
    sched2_line1b,
    sched2_line1c,
    sched2_line1d,
    sched2_line1e,
    sched2_line1y,
    sched2_line1z,
    sched2_line3,
    sched2_line4,
    sched2_line5,
    sched2_line6,
    sched2_line7,
    sched2_line8,
    sched2_line9,
    sched2_line10,
    sched2_line11,
    sched2_line12,
    sched2_line13,
    sched2_line17a,
    sched2_line17b,
    sched2_line17c,
    sched2_line17d,
    sched2_line17e,
    sched2_line17f,
    sched2_line17g,
    sched2_line17h,
    sched2_line17i,
    sched2_line17j,
    sched2_line17k,
    sched2_line17l,
    sched2_line17m,
    sched2_line17n,
    sched2_line17o,
    sched2_line17p,
    sched2_line17q,
    sched2_line17z,
    sched2_line18,
    sched2_line19,
    sched2_line20,
    sched2_line21,
    autoCalculatedAdditionalMedicareTax,
    autoCalculatedNetInvestmentIncomeTax,
    autoCalculatedAdditionalTaxIRA,
    sched3_line1, 
    sched3_line2, 
    sched3_line3, 
    sched3_line4, 
    sched3_line5a, 
    sched3_line5b, 
    sched3_line6a, 
    sched3_line6b, 
    sched3_line6c, 
    sched3_line6d, 
    sched3_line6f, 
    sched3_line6g, 
    sched3_line6h, 
    sched3_line6i, 
    sched3_line6j, 
    sched3_line6k, 
    sched3_line6l, 
    sched3_line6m, 
    sched3_line6z,
    sched3_line7, 
    sched3_line8,
    sched3_line9, 
    sched3_line10, 
    sched3_line11, 
    sched3_line12, 
    sched3_line13a, 
    sched3_line13b, 
    sched3_line13c, 
    sched3_line13d, 
    sched3_line13z,
    sched3_line14, 
    sched3_line15,
    standardDeduction: calculatedStandardDeduction,
    isElectingSalesTax, // <--- Pass this to PDF generator
    line5aAmount, 
    deductibleCashCharity, // <--- Pass this to PDF
    newCharityCarryover,
    deductibleNonCashCharity, // <--- Pass this to PDF
    newNonCashCarryover, 
    deductibleCasualtyLoss, // <--- Pass this to PDF
    agiThreshold, 
    line16Total,              // <--- Pass total to PDF
    deductibleGamblingLosses, // <--- Pass specific amount for description generation
    disasterLossL16,  
    forceItemized,
    selfEmploymentTax, 
    totalSEIncome,     // Needed for PDF Line 2
    totalSEGross,      // Needed for PDF Part II
    totalNotaryExempt, // Needed for PDF Line 3 calc
    totalCRPExempt,
    allowableLoss, // Line 21
    // --- NEW: Form 8949 Arrays ---
      hasAssetSales, 
      form8949Data: {
        A: st_A_adjusted_items, // Only items with adjustments go to PDF
        B: f8949_B_items,
        C: f8949_C_items,
        G: f8949_G_items, // Digital Assets
        H: f8949_H_items,
        I: f8949_I_items,
        
        D: lt_D_adjusted_items, // Only items with adjustments go to PDF
        E: f8949_E_items,
        F: f8949_F_items,
        J: f8949_J_items, // Digital Assets
        K: f8949_K_items,
        L: f8949_L_items
      },
      
      // Schedule D Line Items
      schedD_line1a_colh: schedD_line1a, // Clean Total
      schedD_line1b_colh: schedD_line1b, // Adjusted Total
      schedD_line2_colh: schedD_line2,
      schedD_line3_colh: schedD_line3,
      
      schedD_line8a_colh: schedD_line8a, // Clean Total
      schedD_line8b_colh: schedD_line8b, // Adjusted Total
      schedD_line9_colh: schedD_line9,
      schedD_line10_colh: schedD_line10,
      
      schedD_Line7: schedD_line7,
      schedD_Line15: schedD_line15,
      schedD_Line16: schedD_line16,
      magi_s1a,       // Needed for Schedule 1-A, Line 3
    line13a,        // QBI Deduction (Form 8995)
    line13b,        // Schedule 1-A Total Deduction
    line14,         // Total Deductions (12 + 13a + 13b)
    line15,         // Taxable Income (Updated)
    
    // The main object containing all the _1a calculation details
    sched1A, 
  };
}, [appState, selectedYear]);

  // src/App.js

  // --- COMPREHENSIVE ALERTS & FLAGS ENGINE ---
  const alerts = useMemo(() => {
    const generatedAlerts = [];
    
    // --- FIX: Extract formData from appState ---
    const { formData } = appState; 
    
    // Safety check to prevent crashes if data isn't loaded yet
    if (!formData || !totals) return [];

    const { 
      agi, 
      netBusinessIncome, 
      totalItemizedDeductions, 
      standardDeduction, 
      finalTaxOwed, 
      totalCredits,
      taxLiability 
    } = totals;
    
    const yearData = getTaxConstants(selectedYear);
    const limits = yearData.LIMITS;

    // --- PASS 1: DATA INTEGRITY & MISSING INFO ---
    if (!formData?.ssn && !formData?.skipSSN) {
      generatedAlerts.push("CRITICAL: Taxpayer SSN is missing. The return cannot be filed without it.");
    }
    if (formData?.filingStatus?.includes("Married") && !formData?.spouseSSN && !formData?.skipSSN) {
      generatedAlerts.push("CRITICAL: Spouse SSN is missing.");
    }
    
    if (appState.w2Data) {
        appState.w2Data.forEach((w2, index) => {
          if (!w2.employer) generatedAlerts.push(`W-2 #${index + 1}: Missing Employer Name.`);
          if (!w2.wages || parseFloat(w2.wages) === 0) generatedAlerts.push(`W-2 #${index + 1}: Wages are zero or missing.`);
          if (parseFloat(w2.federalTaxWithheld) > parseFloat(w2.wages)) {
            generatedAlerts.push(`W-2 #${index + 1}: Withholding exceeds wages. Please check your entry.`);
          }
        });
    }

    if (formData?.filingStatus === "Head of Household" && !formData?.qualifyingPersonName && formData?.dependents?.length === 0) {
      generatedAlerts.push("Head of Household claimed but no Qualifying Person or Dependent listed.");
    }

    // --- PASS 2: TAX OPTIMIZATION ---
    if (formData?.itemizedDeductions?.forceItemized && totalItemizedDeductions < standardDeduction) {
      generatedAlerts.push(`OPTIMIZATION: You elected to itemize ($${totalItemizedDeductions.toLocaleString()}), but the Standard Deduction ($${standardDeduction.toLocaleString()}) is higher.`);
    }
    if (!formData?.itemizedDeductions?.forceItemized && totalItemizedDeductions > standardDeduction) {
      generatedAlerts.push(`OPTIMIZATION: Your itemized deductions ($${totalItemizedDeductions.toLocaleString()}) exceed the Standard Deduction. We have automatically applied Schedule A.`);
    }
    
    const cashCharity = parseFloat(formData?.itemizedDeductions?.charitableDonationsCash) || 0;
    if (agi > 0 && cashCharity > (agi * 0.60)) {
      generatedAlerts.push("DEDUCTION LIMIT: Your cash charitable contributions exceed 60% of your AGI. The excess will be carried forward.");
    }

    if (formData?.dependents?.some(d => d.relationship && d.dob) && totalCredits === 0 && agi < 200000) {
      generatedAlerts.push("CREDIT CHECK: You have dependents but no Child Tax Credit calculated. Check dependent ages and SSNs.");
    }

    // --- PASS 3: AUDIT RISKS ---
    (appState.scheduleCData || []).forEach((biz, i) => {
      const gross = parseFloat(biz.grossReceipts) || 0;
      const expenses = (parseFloat(biz.advertising) || 0) + (parseFloat(biz.carAndTruckExpenses) || 0) + (parseFloat(biz.supplies) || 0) + (parseFloat(biz.legalAndProfessional) || 0);
      
      if (gross > 0 && expenses > gross) {
        generatedAlerts.push(`AUDIT RISK (Business ${i+1}): You are reporting a business loss. Consistent losses may cause the IRS to classify your business as a "Hobby."`);
      }
      
      const meals = parseFloat(biz.meals) || 0;
      if (meals > 0 && meals > (gross * 0.5)) {
        generatedAlerts.push(`AUDIT RISK (Business ${i+1}): Meal expenses are very high relative to income (>50%).`);
      }

      const miles = parseFloat(biz.vehicleInfo?.businessMiles) || 0;
      if (miles > 0 && !biz.vehicleInfo?.hasEvidence) {
        generatedAlerts.push(`COMPLIANCE (Business ${i+1}): You claimed vehicle mileage but indicated you do not have evidence. The IRS requires written logs.`);
      }
    });

    (appState.rentalData || []).forEach((prop, i) => {
      const daysRented = parseFloat(prop.fairRentalDays) || 0;
      const daysPersonal = parseFloat(prop.personalUseDays) || 0;
      if (daysRented > 0 && daysPersonal > 14 && daysPersonal > (daysRented * 0.1)) {
        generatedAlerts.push(`RENTAL LIMIT (Property ${i+1}): High personal use detected. This is considered a "Vacation Home," and rental losses are limited.`);
      }
    });

    // --- PASS 4: 2025 NEW LAW COMPLIANCE ---
    if (!formData?.hasSchedule1A) {
      if (formData?.isOver65 || formData?.spouseIsOver65) {
        generatedAlerts.push("NEW LAW: You or your spouse are over 65. You likely qualify for the new 'Senior Bonus' deduction on Schedule 1-A.");
      }
      if ((parseFloat(formData?.tipsW2) > 0 || parseFloat(formData?.tips4137) > 0)) {
        generatedAlerts.push("NEW LAW: You have tip income. You may qualify for the 'No Tax on Tips' deduction on Schedule 1-A.");
      }
      if ((parseFloat(formData?.overtimeW2) > 0 || parseFloat(formData?.overtime1099) > 0)) {
        generatedAlerts.push("NEW LAW: You have overtime pay. You may qualify for the 'No Tax on Overtime' deduction on Schedule 1-A.");
      }
    }

    // --- PASS 5: PAYMENTS & PENALTIES ---
    if (totals.line38 > 0) {
      generatedAlerts.push(`PENALTY: An estimated tax penalty of $${totals.line38.toFixed(2)} has been applied. If you made quarterly payments, enter them in 'Deductions & Credits'.`);
    }
    
    if (finalTaxOwed > 10000) {
      generatedAlerts.push("HIGH BALANCE: You owe over $10,000. Consider setting up an Installment Agreement (Form 9465).");
    }

    if (totals.line35a > 10000) {
      generatedAlerts.push("LARGE REFUND: Your refund is over $10,000. This may trigger additional manual review by the IRS.");
    }

    if (totals.sched3_line11 > 0) {
      generatedAlerts.push(`NOTICE: You overpaid Social Security tax. We have claimed a credit of $${totals.sched3_line11.toFixed(2)} on Schedule 3.`);
    }

    return generatedAlerts;
  }, [appState, totals, selectedYear]);

  // --- COMPREHENSIVE CALCULATION AUDIT ---
  const calculationCheck = useMemo(() => {
    // --- FIX: Extract formData from appState ---
    const { formData } = appState;

    // 1. Verify Total Income (Line 9)
    const audit_w2 = totals.line1z; 
    const audit_interest_divs = totals.line2b + totals.line3b;
    const audit_ira_pension_ss = totals.line4b + totals.line5b + totals.line6b;
    const audit_capital_gains = totals.line7;
    const audit_schedule1 = totals.sched1_line10; 
    
    const calculated_TotalIncome = audit_w2 + audit_interest_divs + audit_ira_pension_ss + audit_capital_gains + audit_schedule1;
    const isIncomeValid = Math.abs(calculated_TotalIncome - totals.totalIncome_line9) < 0.05;

    // 2. Verify AGI (Line 11)
    const audit_adjustments = totals.sched1_line26; 
    const calculated_AGI = calculated_TotalIncome - audit_adjustments;
    const isAGIValid = Math.abs(calculated_AGI - totals.agi) < 0.05;

    // 3. Verify Taxable Income (Line 15)
    const audit_deductions = totals.line12 + totals.line13a + totals.line13b;
    const calculated_Taxable = Math.max(0, calculated_AGI - audit_deductions);
    const isTaxableValid = Math.abs(calculated_Taxable - totals.taxableIncome) < 0.05;

    // 4. Verify Total Tax (Line 24)
    const audit_tax_line16 = parseFloat(totals.line16) || 0;
    const audit_sched2_part1 = totals.sched2_line3;
    const audit_credits_nonrefundable = totals.line21; 
    const audit_sched2_part2 = totals.sched2_line21; 
    
    const audit_line18 = audit_tax_line16 + audit_sched2_part1;
    const audit_line22 = Math.max(0, audit_line18 - audit_credits_nonrefundable);
    const calculated_TotalTax = audit_line22 + audit_sched2_part2;
    
    const isTaxValid = Math.abs(calculated_TotalTax - totals.taxLiability) < 0.05;

    // 5. Verify Payments (Line 33)
    const audit_withholding = totals.line25d;
    const audit_estimated = totals.line26;
    const audit_refundable_credits = totals.line32; 
    const calculated_Payments = audit_withholding + audit_estimated + audit_refundable_credits;
    
    const isPaymentsValid = Math.abs(calculated_Payments - totals.line33) < 0.05;

    // 6. Final Reconciliation
    const calculated_Owed = calculated_TotalTax - calculated_Payments;
    const actual_Owed_from_Totals = totals.line37 - totals.line35a; 
    const final_math_balance = calculated_Owed + totals.line38;
    const isFinalBalanceValid = Math.abs(final_math_balance - actual_Owed_from_Totals) < 0.05;

    // 7. Check for Missing Critical Data
    const missingData = [];
    if (!formData?.ssn && !formData?.skipSSN) missingData.push("SSN");
    if (formData?.filingStatus?.includes("Married") && !formData?.spouseSSN && !formData?.skipSSN) missingData.push("Spouse SSN");
    if (totals.netBusinessIncome !== 0 && !formData?.showScheduleCDetails && !formData?.manualBusinessIncome) missingData.push("Business Details");
    
    const potentialMissingPayments = (totals.taxLiability > 1000 && totals.line26 === 0 && totals.line25d === 0);

    const isFullyValid = isIncomeValid && isAGIValid && isTaxableValid && isTaxValid && isPaymentsValid && isFinalBalanceValid && missingData.length === 0;

    return {
      isValid: isFullyValid,
      status: isFullyValid ? "Math Verified" : "Review Needed",
      details: {
        income: isIncomeValid,
        agi: isAGIValid,
        tax: isTaxValid,
        payments: isPaymentsValid,
        balance: isFinalBalanceValid
      },
      missingData,
      potentialMissingPayments,
      calculated_TotalTax,
      calculated_Payments
    };
  }, [totals, appState]);

  // --- EVENT HANDLERS ---
  const updateState = (key, value) => {
    setAppState(prevState => ({ ...prevState, [key]: value }));
  };

  const handleAddItem = (key) => {
    const newItem = getNewItemTemplate(key);
    updateState(key, [...appState[key], newItem]);
  };

  // NEW: Generic handler to delete an item from any data array
  const handleDeleteItem = (key, idToDelete) => {
    const updatedData = appState[key].filter(item => item.id !== idToDelete);
    updateState(key, updatedData);
  };

   const handleGridEdit = (key, id, field, value) => {
    const updatedData = appState[key].map(item => {
      if (item.id === id) {
        // Create a copy of the item to modify
        const newItem = { ...item };
        
        // Handle nested fields like 'expenses.advertising'
        if (field.includes('.')) {
          const [parent, child] = field.split('.');
          newItem[parent] = { ...newItem[parent], [child]: value };
        } else {
          newItem[field] = value;
        }
        return newItem;
      }
      return item;
    });
    updateState(key, updatedData);
  };

  const toggleChat = () => {
    // Update the isChatVisible property within our main appState
    updateState('isChatVisible', !appState.isChatVisible);
  };

  // src/App.js

// --- REPLACE your old handleFormChange with this ---
const handleFormChange = (e) => {
  const { name, value, type, checked } = e.target;
  const newValue = type === 'checkbox' ? checked : value;
  const newFormData = JSON.parse(JSON.stringify(appState.formData));
  
  if (['medicalExpenses', 'stateAndLocalTaxes', 'mortgageInterest', 'charitableDonations'].includes(name)) {
    newFormData.itemizedDeductions[name] = newValue;
  } else if (name.includes('.')) {
    const parts = name.split('.');
    let current = newFormData;
    
    // Navigate to the parent object
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    
    // Set the final value
    current[parts[parts.length - 1]] = newValue;
  } else {
    newFormData[name] = newValue;
  }
  
  updateState('formData', newFormData);
};

  const handleRentalChange = (params) => {
    const { data, colDef, newValue } = params;
    const updatedRentalData = appState.rentalData.map(row => {
      if (row.id !== data.id) return row;
      const newRow = JSON.parse(JSON.stringify(row));
      const field = colDef.field;
      if (field.startsWith('expenses.')) {
        newRow.expenses[field.split('.')[1]] = parseFloat(newValue) || 0;
      } else {
        newRow[field] = parseFloat(newValue) || 0;
      }
      return newRow;
    });
    updateState('rentalData', updatedRentalData);
  };

  const handleInvestmentImportRequest = (file) => {
    const requiredFields = ['Symbol', 'Date Acquired', 'Date Sold', 'Cost Basis', 'Proceeds'];
    handleSmartImport(file, requiredFields, handleInvestmentImport);
  };

  const handleExpenseImportRequest = (file) => {
    const requiredFields = ['Date', 'Description', 'Amount'];
    handleSmartImport(file, requiredFields, handleExpenseImport);
  };
  
  const handleW2ImportRequest = (file) => {
    const requiredFields = ['Employer', 'Wages', 'Federal Tax Withheld'];
    handleSmartImport(file, requiredFields, handleW2Import);
  };

  // For these, we can keep the manual mapper for now, or expand the regex dictionary later.
  const handleIncomeImportRequest = (file) => {
    // This uses the same logic as Expenses
    const requiredFields = ['Date', 'Description', 'Amount'];
    handleSmartImport(file, requiredFields, handleIncomeImport);
  };

  const handleRentalImportRequest = (file) => {
    const requiredFields = ['Property Description', 'Rents Received', 'Property Taxes'];
    handleSmartImport(file, requiredFields, handleRentalImport);
  };

  const handleAssetImportRequest = (file) => {
    const requiredFields = ['Asset Description', 'Date Acquired', 'Cost', 'Useful Life'];
    handleSmartImport(file, requiredFields, handleAssetImport);
  };
  const handleInvestmentImport = (mapping, rawData) => {
    const mappedData = rawData.map((row, i) => ({
      id: `inv-csv-${Date.now()}-${i}`,
      timestamp: new Date().toISOString(),
      transactionDate: row[mapping['Date Sold']] || '',
      description: `${row[mapping['Quantity']] || ''} shares of ${row[mapping['Symbol']] || ''}`,
      purchaseDate: row[mapping['Date Acquired']] || '',
      costBasis: parseFloat(row[mapping['Cost Basis']] || 0),
      saleDate: row[mapping['Date Sold']] || '',
      saleProceeds: parseFloat(row[mapping['Proceeds']] || 0),
    }));
    updateState('investmentsData', [...appState.investmentsData, ...mappedData]);
  };

  const handleExpenseImport = (mapping, rawData) => {
    const mappedData = rawData.map((row, i) => ({
      id: `exp-csv-${Date.now()}-${i}`,
      timestamp: new Date().toISOString(),
      transactionDate: row[mapping['Date']] || '',
      isDeductible: false,
      category: 'Other',
      description: row[mapping['Description']] || '',
      amount: Math.abs(parseFloat(row[mapping['Amount']] || 0)),
    }));
    updateState('expensesData', [...appState.expensesData, ...mappedData]);
  };

  const handleW2Import = (mapping, rawData) => {
    const mappedData = rawData.map((row, i) => ({
      id: `w2-csv-${Date.now()}-${i}`,
      timestamp: new Date().toISOString(),
      transactionDate: '',
      employer: row[mapping['Employer']] || '',
      wages: parseFloat(row[mapping['Wages']] || 0),
      federalTaxWithheld: parseFloat(row[mapping['Federal Tax Withheld']] || 0),
    }));
    updateState('w2Data', [...appState.w2Data, ...mappedData]);
  };

  const handleIncomeImport = (mapping, rawData) => {
    const mappedData = rawData.map((row, i) => ({
      id: `inc-csv-${Date.now()}-${i}`,
      timestamp: new Date().toISOString(),
      transactionDate: row[mapping['Date']] || '',
      category: 'Freelance',
      description: row[mapping['Description']] || '',
      amount: parseFloat(row[mapping['Amount']] || 0),
      federalTax: 15, localTax: 5
    }));
    updateState('incomeData', [...appState.incomeData, ...mappedData]);
  };

  const handleRentalImport = (mapping, rawData) => {
    const mappedData = rawData.map((row, i) => ({
      id: `rent-csv-${Date.now()}-${i}`,
      timestamp: new Date().toISOString(),
      transactionDate: '',
      description: row[mapping['Property Description']] || '',
      rentsReceived: parseFloat(row[mapping['Rents Received']] || 0),
      expenses: {
        advertising: parseFloat(row[mapping['Advertising']] || 0),
        repairs: parseFloat(row[mapping['Repairs']] || 0),
        insurance: parseFloat(row[mapping['Insurance']] || 0),
        propertyTaxes: parseFloat(row[mapping['Property Taxes']] || 0),
      }
    }));
    updateState('rentalData', [...appState.rentalData, ...mappedData]);
  };

  const handleAssetImport = (mapping, rawData) => {
    const mappedData = rawData.map((row, i) => ({
      id: `as-csv-${Date.now()}-${i}`,
      timestamp: new Date().toISOString(),
      transactionDate: row[mapping['Date Acquired']] || '',
      description: row[mapping['Asset Description']] || '',
      purchaseDate: row[mapping['Date Acquired']] || '',
      cost: parseFloat(row[mapping['Cost']] || 0),
      usefulLife: parseInt(row[mapping['Useful Life']] || 5, 10),
    }));
    updateState('assetsData', [...appState.assetsData, ...mappedData]);
  };

  const handleReadyToFile = () => setActiveTab('File');

  const handleStatusSelect = (newStatus) => {
  const event = { target: { name: 'filingStatus', value: newStatus } };
  handleFormChange(event);
  // Don't close the modal here - let the modal component handle when to close
};

  // In src/App.js, inside the App component


  return (
    
    <>
      <div className="page-container">
        <SideNav
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          filingStatus={appState.formData.filingStatus}
          // CHANGED: Added this line
          userName={`${appState.formData.firstName || ''} ${appState.formData.lastName || ''}`.trim()}
          onOpenModal={() => setIsStatusModalOpen(true)}
          onReady={handleReadyToFile}
        />

        <main className="page-content">

          {/* --- UPDATED GLOBAL HEADER -- */}
  <div className="app-global-header">
    <div className="header-brand">
      {/* Replaced text with Image */}
      <img 
        src={logo} 
        alt="Redsystem TaxFlow" 
        style={{ height: '50px', width: 'auto' }} 
      />
    </div>
    <div className="header-meta">
      <p>2025 Federal Tax Estimator & Filer</p>
    </div>
  </div>
          
          <div className="content-card">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              
              {/* 1. Date Picker (Left) */}
              <div className="date-navigation-container">
                {selectedYear && (
                  <>
                    <DateRangePicker date={dateRange} onDateChange={handleDateChange} />
                   
                  </>
                )}
              </div>

              {/* 2. Title (Moved to Middle/Left) */}
              <div style={{ marginLeft: '1.5rem', marginRight: 'auto' }}>
                <h2>{activeTab}</h2>
              </div>

            </div>
          </div>

          {activeTab === 'Income' && (
            <div className="content-card">
              <IncomeSourceSelector 
                formData={appState.formData}      // <--- ADDED
                onDataChange={handleFormChange}   // <--- ADDED
                visibility={appState.formData.incomeVisibility || { w2: true }} 
                onToggle={(key, value) => {
                  handleFormChange({
                    target: {
                      name: `incomeVisibility.${key}`,
                      checked: value,
                      type: 'checkbox'
                    }
                  });
                }}
              />
              <W2Grid  formData={appState.formData}       // <--- WAS MISSING
    onDataChange={handleFormChange}  rowData={filteredW2Data} onFileParsed={handleW2ImportRequest} onAddItem={() => handleAddItem('w2Data')} onDeleteItem={(id) => handleDeleteItem('w2Data', id)} onGridEdit={(id, field, val) => handleGridEdit('w2Data', id, field, val)} />
              <IncomeGrid  formData={appState.formData}       // <--- WAS MISSING
    onDataChange={handleFormChange} 
    rowData={filteredIncomeData} onFileParsed={handleIncomeImportRequest} onAddItem={() => handleAddItem('incomeData')} onDeleteItem={(id) => handleDeleteItem('incomeData', id)} onGridEdit={(id, field, val) => handleGridEdit('incomeData', id, field, val)} />
              <HouseholdWagesGrid
              rowData={appState.householdWagesData}
              onAddItem={() => handleAddItem('householdWagesData')}
              onDeleteItem={(id) => handleDeleteItem('householdWagesData', id)}
              onGridEdit={(id, field, val) => handleGridEdit('householdWagesData', id, field, val)}
              formData={appState.formData}
              onDataChange={handleFormChange}
            />
            <TipIncomeGrid
              rowData={appState.tipIncomeData}
              onAddItem={() => handleAddItem('tipIncomeData')}
              onDeleteItem={(id) => handleDeleteItem('tipIncomeData', id)}
              onGridEdit={(id, field, val) => handleGridEdit('tipIncomeData', id, field, val)}
              formData={appState.formData}
              onDataChange={handleFormChange}
            />
            <WaiverPaymentsGrid
              rowData={appState.waiverPaymentsData}
              onAddItem={() => handleAddItem('waiverPaymentsData')}
              onDeleteItem={(id) => handleDeleteItem('waiverPaymentsData', id)}
              onGridEdit={(id, field, val) => handleGridEdit('waiverPaymentsData', id, field, val)}
              formData={appState.formData}       // <--- ADDED
              onDataChange={handleFormChange}    // <--- ADDED
            />
            <Form8919Questions 
              formData={appState.formData} 
              onDataChange={handleFormChange} 
            />
            <OtherIncome 
            formData={appState.formData} 
            onDataChange={handleFormChange} 
            totals={totals}
          />

          {/* --- ADD THE NEW COMPONENT HERE --- */}
          <CombatPay
            formData={appState.formData}
            onDataChange={handleFormChange}
          />
          <InterestIncome
            formData={appState.formData}
            onDataChange={handleFormChange}
          />
              <RentalIncomeGrid formData={appState.formData}       // <--- ADD THIS
  onDataChange={handleFormChange}    // <--- ADD THIS
   rowData={filteredRentalData} onCellValueChanged={handleRentalChange} onFileParsed={handleRentalImportRequest} onAddItem={() => handleAddItem('rentalData')} onGridEdit={(id, field, val) => handleGridEdit('rentalData', id, field, val)} />
                <BusinessIncome 
                scheduleCData={appState.scheduleCData} 
                onUpdate={handleScheduleCUpdate} 
                formData={appState.formData}       // <--- Pass formData
  onDataChange={handleFormChange}
              />
              <ScheduleE 
  data={appState.scheduleEData} 
  onUpdate={handleScheduleEUpdate} 
  formData={appState.formData}      // <--- REQUIRED for Manual Entry
                  onDataChange={handleFormChange}
/>
            </div>
            
          )}

          {activeTab === 'Expenses & Assets' && (
            <div className="content-card">
              <ExpensesGrid 
                formData={appState.formData}
                onDataChange={handleFormChange} 
              />
              <AssetsGrid 
                formData={appState.formData}
                onDataChange={handleFormChange}
              />
            </div>
          )}

          {activeTab === 'Investments' && (
            <div className="content-card">
              <InvestmentsGrid
                rowData={filteredInvestmentsData}
                onFileParsed={handleInvestmentImportRequest}
                formData={appState.formData}
                onFormChange={handleFormChange}
                onAddItem={() => handleAddItem('investmentsData')}
                onDeleteItem={(id) => handleDeleteItem('investmentsData', id)}
                onGridEdit={(id, field, val) => handleGridEdit('investmentsData', id, field, val)}
                totals={totals}
              />
            </div>
          )}

          {activeTab === 'Deductions & Credits' && (
            <div className="content-card">
              <DependentsForm
            formData={appState.formData}
            onDependentsUpdate={handleDependentsUpdate}
            onProvidersUpdate={handleCareProvidersUpdate}
            totalW2Wages={totals.totalW2Wages}
            netBusinessIncome={totals.netBusinessIncome}
            onDependentCareBenefitsUpdate={handleDependentCareBenefitsUpdate}
            adoptionWorksheet={totals.adoptionWorksheet}
            onDataChange={handleFormChange}
            taxYear={selectedYear || new Date().getFullYear()}
          />
          <Schedule1AInputs 
      formData={appState.formData} 
      onDataChange={handleFormChange}
      totals={totals}
    />
              <ItemizedDeductions 
                data={appState.formData.itemizedDeductions} 
                formData={appState.formData} // <--- ADD THIS PROP
                expensesData={appState.expensesData}
                onDataChange={handleFormChange} 
                agi={totals.agi} 
                errors={errors} 
                taxYear={selectedYear}
                standardDeduction={totals.standardDeduction}
                totalItemized={totals.totalItemizedDeductions}
                deductionType={totals.deductionType}
                investmentIncome={(parseFloat(appState.formData.taxableInterest) || 0) + 
                                  (parseFloat(appState.formData.ordinaryDividends) || 0) - 
                                  (parseFloat(appState.formData.qualifiedDividends) || 0)}
              />
              <CreditsAndAdjustments data={appState.formData} onDataChange={handleFormChange} errors={errors} totals={totals} />
            </div>
          )}

          {activeTab === 'File' && (
            <div className="content-card">
              <FilingPage totals={totals}
              formData={appState.formData}       // <--- ADD THIS
              scheduleCData={appState.scheduleCData}
              investmentsData={appState.investmentsData}
              scheduleEData={appState.scheduleEData} 
              onDataChange={handleFormChange} />
            </div>
          )}

          <div className="content-card">
            <TotalsSummary totals={totals} />
          </div>
          
          <Alerts alerts={alerts} />
          <AuditDefenseNote />
          
        </main>
        {/* 
        <div
          className={`
            ${isChatSticky ? 'chat-column-placeholder' : ''} 
            ${!appState.isChatVisible ? 'collapsed' : ''}
          `}
        ></div>

        <div
          ref={chatColumnRef}
          className={`
            chat-column 
            ${!appState.isChatVisible ? 'collapsed' : ''} 
            ${isChatSticky ? 'is-sticky' : ''}
          `}
        >
          <button onClick={toggleChat} className="chat-toggle-button" title={appState.isChatVisible ? "Collapse Chat" : "Expand Chat"}>
            {appState.isChatVisible ? <ChevronsRight size={20} /> : <ChevronsLeft size={20} />}
          </button>
          <div className="chat-interface">
            <ChatInterface />
          </div>
        </div>
        */}
      </div>

      {/* 
      <button className="chat-fab" onClick={() => setIsChatModalOpen(true)}>
        <MessageSquare size={24} />
      </button>
      <ChatModal isOpen={isChatModalOpen} onClose={() => setIsChatModalOpen(false)} />
      */}

      {isStatusModalOpen && (
        <FilingStatusModal
          formData={appState.formData}
          onDataChange={handleFormChange}
          onStatusSelect={handleStatusSelect}
          onClose={() => setIsStatusModalOpen(false)}
          
          // --- FIX: Accept the status argument to ensure it saves correctly ---
          onDone={(confirmedStatus) => {
            setIsStatusModalOpen(false);
            setAppState(prevState => ({
              ...prevState,
              formData: {
                ...prevState.formData,
                // Use the confirmed status passed from the modal, 
                // or fall back to the current state if missing.
                filingStatus: confirmedStatus || prevState.formData.filingStatus,
                isFilingStatusConfirmed: true 
              }
            }));
          }}
        />
      )}

      
      <CalculationCheck 
        isValid={calculationCheck.isValid} 
        status={calculationCheck.status}
        details={calculationCheck.details}
        missingData={calculationCheck.missingData}
        potentialMissingPayments={calculationCheck.potentialMissingPayments}
        calculated_TotalTax={calculationCheck.calculated_TotalTax}
        calculated_Payments={calculationCheck.calculated_Payments}
      />
      <Footer />
    </>
  );
}

export default App;
